import {createRetryIdentity} from '../../features/network/requestIdentity';
import {PersonalOrderGate} from './ProfileProtection';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useStationMode } from '../Common/useStationMode';
import { AuthContext, NumberSelector, playSucessSound, SuccessNotification } from '../../App';
import { getBerlinDate } from '../../utils/berlinDate';
import { buildMenuCategoryOrderPayload, getVisibleMenuCategories, menuCategoryOrderTerms } from './menuCategories';
import { mealLockNotice } from './mealLockNotice';
import { buildMealEditPayload, existingOrderFormData, existingVariantCounts, isButterOnlyEdit } from './orderEditing';
import { getNightOnlyMealVisibility } from './nightOnlyOrdering';
import {BalanceDeadlineMessage, useBalanceDeadline} from '../../features/payment/BalanceDeadlineNotice';
import BalanceOrderGate from './BalanceOrderGate';
import {useOrderMenuData, finitePrice, validPrice, priceLoadReason} from '../../services/orderMenuCache';
import {orderPriceAvailability} from '../../services/orderPriceAvailability';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const EMPTY_MENU = [];
const EMPTY_SETTINGS = {};

const EmployeeMenu = ({ employee, onClose, onOrderComplete, fetchEmployees, orderToken, onProtectionRequired }) => {
  const [activeCategory, setActiveCategory] = useState(null); // Start with null until config is loaded
  
  // REF für sofortige Sperre (verhindert React State Async-Problem)
  const isSubmittingRef = useRef(false);
  const menuOpenRef = useRef(true);
  useEffect(() => {
    menuOpenRef.current = true;
    return () => { menuOpenRef.current = false; };
  }, []);

  // Scroll to top when changing categories
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeCategory]);
  const [customCategoryItems, setCustomCategoryItems] = useState({});
  const [menuNotice, setMenuNotice] = useState('');
  const [order, setOrder] = useState({
    breakfast_items: [],
    drink_items: {},
    sweet_items: {}
  });
  const [breakfastFormData, setBreakfastFormData] = useState(null);
  const [existingBreakfastOrder, setExistingBreakfastOrder] = useState(null);
  const [isEditingBreakfastOrder, setIsEditingBreakfastOrder] = useState(false);
  const editMutationRef = useRef(null);
  const networkOrderIdentity = useRef(createRetryIdentity());
  const postOrder = async payload => {
    const body = employee.is_external_guest ? {...payload, network_request_id: networkOrderIdentity.current.forPayload(payload)} : payload;
    const result = await axios.post(`${API}/orders`, body, {headers: {'X-Profile-Order-Token': orderToken || ''}});
    networkOrderIdentity.current.clear();
    return result;
  };
  const [isLoadingExistingOrders, setIsLoadingExistingOrders] = useState(true);
  const [mealOrderingStatus, setMealOrderingStatus] = useState({
    meals: {
      breakfast: { is_closed: false },
      lunch: { is_closed: false },
      dinner: { is_closed: false },
    },
  });
  const [nightOnly, setNightOnly] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  // NEU: Cooldown State für Doppelbuchungs-Schutz
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  // NEU: Key to force remount of order forms after successful order
  const [orderResetKey, setOrderResetKey] = useState(0);

  const { currentDepartment } = React.useContext(AuthContext);
  const [coffeeFlat,setCoffeeFlat] = useState(null);
  useEffect(() => {
    let live=true;
    const load=()=>axios.get(`${API}/coffee-flat/${currentDepartment.department_id}/${employee.id}`)
      .then(r=>{if(live)setCoffeeFlat(r.data);}).catch(()=>{if(live)setCoffeeFlat(null);});
    load(); window.addEventListener('coffee-flat-changed',load);
    return ()=>{live=false;window.removeEventListener('coffee-flat-changed',load);};
  },[currentDepartment.department_id,employee.id]);
  const menuData = useOrderMenuData(currentDepartment.department_id);
  const departmentConfig = menuData.config.data;
  const breakfastMenu = menuData.display.rolls || EMPTY_MENU;
  const toppingsMenu = menuData.display.toppings || EMPTY_MENU;
  const lunchSettings = menuData.display.extras || EMPTY_SETTINGS;
  const menuCategories = menuData.categories.data || EMPTY_MENU;
  const todayLunchName = menuData.display.lunch?.lunch_name || '';
  const todayLunchPrice = menuData.display.lunch?.lunch_price ?? 0;
  const todayDinnerName = menuData.display.dinner?.dinner_name || '';
  const todayDinnerPrice = menuData.display.dinner?.dinner_price ?? 0;
  const lunchVariants = menuData.display.lunchVariants?.variants || EMPTY_MENU;
  const dinnerVariants = menuData.display.dinnerVariants?.variants || EMPTY_MENU;
  const priceAvailability = orderPriceAvailability(menuData, departmentConfig, breakfastFormData, activeCategory, order, customCategoryItems, nightOnly);
  const requiredResources = priceAvailability.required;
  const menuMissing = priceAvailability.blocked;
  const renderMissing = !departmentConfig || (activeCategory !== 'breakfast' && !menuData.categories.data);
  const menuError = priceAvailability.blocked;
  const priceIssues = Object.values(priceAvailability.issues);
  const unavailableTopping = activeCategory === 'breakfast' && (breakfastFormData?.toppings || [])
    .some(type => type && !toppingsMenu.some(item => (item.topping_type || item.id) === type));
  const incompleteBreakfast = activeCategory === 'breakfast' && breakfastFormData === undefined;
  const retryMenu = async () => {
    setMenuNotice('');
    await Promise.allSettled(priceAvailability.retry.map(kind => menuData.refresh([kind])));
  };

  const deadline = useBalanceDeadline(employee.id, currentDepartment.department_id);
  const debtOrderLocked = deadline.unavailable || deadline.data?.blocked;
  
  // REMOVED: Automatic mount reset - this caused race conditions with loadExistingOrdersForToday
  // The existing order data should be loaded and preserved, not reset on mount.
  // Reset only happens explicitly after successful order submission.
  
  // Handle modal close - reset state before closing
  const handleClose = useCallback(() => {
    menuOpenRef.current = false;
    React.startTransition(() => {
      setOrder({
        breakfast_items: [],
        drink_items: {},
        sweet_items: {}
      });
      setCustomCategoryItems({});
      setBreakfastFormData(null);
      setExistingBreakfastOrder(null);
      setIsEditingBreakfastOrder(false);
      editMutationRef.current = null;
      setOrderResetKey(prev => prev + 1);
    });
    onClose();
  }, [onClose]);

  const visibleMenuCategories = useMemo(
    () => getVisibleMenuCategories(menuCategories, departmentConfig || {}),
    [menuCategories, departmentConfig]
  );

  const selectedMenuCategory = useMemo(() => (
    visibleMenuCategories.find(category => (
      category.system_key === activeCategory || category.id === activeCategory
    )) || null
  ), [visibleMenuCategories, activeCategory]);

  const existingBreakfastFormValues = useMemo(
    () => existingOrderFormData(existingBreakfastOrder),
    [existingBreakfastOrder],
  );
  
  // Set initial active category based on enabled categories AFTER config is loaded
  useEffect(() => {
    if (departmentConfig && activeCategory === null) {
      // Determine first available category
      if (departmentConfig.is_breakfast_lunch_enabled) {
        setActiveCategory('breakfast');
      } else if (visibleMenuCategories.length > 0) {
        setActiveCategory(visibleMenuCategories[0].system_key || visibleMenuCategories[0].id);
      }
    }
  }, [departmentConfig, activeCategory, visibleMenuCategories]);
  
  // Stable category change handler with useCallback
  const handleCategoryChange = useCallback((category) => {
    if (isSubmittingRef.current) return;
    if (category !== 'breakfast') {
      setIsEditingBreakfastOrder(false);
      editMutationRef.current = null;
    }
    setActiveCategory(category);
  }, []);

  // Load existing orders for today when component mounts
  useEffect(() => {
    loadExistingOrdersForToday();
  }, [employee.id]);

  const loadExistingOrdersForToday = async () => {
    try {
      setIsLoadingExistingOrders(true);
      const response = await axios.get(`${API}/employees/${employee.id}/orders`, {timeout: 10000});
      const orders = response.data.orders || [];
      
      // Filter orders for today AND current department (ERWEITERT: Abteilungsbasierte Filterung)
      const today = new Date().toDateString();
      // Get all today's orders (including cancelled ones) for display purposes
      const allTodaysOrders = orders.filter(order => {
        const orderDate = new Date(order.timestamp).toDateString();
        return orderDate === today && order.department_id === currentDepartment.department_id; // ERWEITERT: Nur aktuelle Abteilung
      });

      // Get only non-cancelled orders for form pre-filling
      const activeTodaysOrders = allTodaysOrders.filter(order => !order.is_cancelled);

      // CRITICAL FIX: Only pre-populate BREAKFAST for editing
      // Do NOT load drinks/sweets into order state - they should start fresh for each new order!
      const breakfastOrders = activeTodaysOrders.filter(order => order.order_type === 'breakfast');
      
      // If there's breakfast data, store the full order for summary display
      if (breakfastOrders.length > 0 && breakfastOrders[0].breakfast_items?.length > 0) {
        console.log('Existing breakfast order found:', breakfastOrders[0].id);
        setBreakfastFormData(existingOrderFormData(breakfastOrders[0]));
        setExistingBreakfastOrder(breakfastOrders[0]); // Store full order with ID
        setIsEditingBreakfastOrder(false);
        
        // Set order state ONLY for breakfast (for update logic)
        setOrder({
          breakfast_items: breakfastOrders[0].breakfast_items,
          drink_items: {},  // Always start empty!
          sweet_items: {}   // Always start empty!
        });
      } else {
        // No breakfast order exists - ensure clean state
        setBreakfastFormData(null);
        setExistingBreakfastOrder(null);
        setIsEditingBreakfastOrder(false);
        setOrder({
          breakfast_items: [],
          drink_items: {},
          sweet_items: {}
        });
      }

    } catch (error) {
      console.error('Fehler beim Laden bestehender Bestellungen:', error);
      // Continue with empty order if loading fails
    } finally {
      setIsLoadingExistingOrders(false);
    }
  };

  useEffect(() => {
    fetchMealOrderingStatus();
    fetchNightOnlyStatus();
  }, [currentDepartment]);

  const fetchNightOnlyStatus = async () => {
    if (!currentDepartment?.department_id) return;
    try {
      const response = await axios.get(
        `${API}/daily-night-only/${currentDepartment.department_id}/${getBerlinDate()}`
      );
      setNightOnly(Boolean(response.data?.night_only));
    } catch (error) {
      console.error('Nachtdienst-Modus konnte nicht geladen werden:', error);
      setNightOnly(false);
    }
  };

  const fetchMealOrderingStatus = async () => {
    try {
      if (!currentDepartment?.department_id) {
        return;
      }
      const response = await axios.get(`${API}/meal-ordering-status/${currentDepartment.department_id}`);
      setMealOrderingStatus(response.data);
    } catch (error) {
      console.error('Fehler beim Laden des Mahlzeiten-Status:', error);
    }
  };

  // Create dynamic labels from menu data
  const rollTypeLabels = {
    'weiss': 'Helles Brötchen',
    'koerner': 'Körnerbrötchen'
  };

  // Use custom names from menu if available, otherwise fall back to defaults
  const toppingLabels = {};
  toppingsMenu.forEach(item => {
    toppingLabels[item.topping_type || item.id] = item.name || {
      'ruehrei': 'Rührei',
      'spiegelei': 'Spiegelei', 
      'eiersalat': 'Eiersalat',
      'salami': 'Salami',
      'schinken': 'Schinken',
      'kaese': 'Käse',
      'butter': 'Butter'
    }[item.topping_type] || item.topping_type;
  });

  // Fallback toppings if menu is empty
  const defaultToppingLabels = {
    'ruehrei': 'Rührei',
    'spiegelei': 'Spiegelei',
    'eiersalat': 'Eiersalat',
    'salami': 'Salami',
    'schinken': 'Schinken',
    'kaese': 'Käse',
    'butter': 'Butter'
  };

  const finalToppingLabels = Object.keys(toppingLabels).length > 0 ? toppingLabels : defaultToppingLabels;

  const handleBreakfastFormSubmit = useCallback((breakfastData) => {
    setBreakfastFormData(breakfastData);
  }, []);

  const addBreakfastItem = (totalHalves, whiteHalves, seededHalves, selectedToppings, hasLunch, boiledEggs, friedEggs, scrambledEggs, totalCost) => {
    const newItem = {
      total_halves: totalHalves,
      white_halves: whiteHalves,
      seeded_halves: seededHalves,
      toppings: selectedToppings,
      has_lunch: hasLunch,
      boiled_eggs: boiledEggs,
      fried_eggs: friedEggs,
      scrambled_eggs: scrambledEggs,
      has_coffee: false
    };
    setOrder(prev => ({
      ...prev,
      breakfast_items: [...prev.breakfast_items, newItem]
    }));
  };

  const updateDrinkQuantity = useCallback((drinkId, quantity) => {
    setOrder(prev => {
      const newDrinkItems = { ...prev.drink_items };
      const parsedQuantity = parseInt(quantity) || 0;
      
      if (parsedQuantity === 0) {
        // Remove item if quantity is 0
        delete newDrinkItems[drinkId];
      } else {
        newDrinkItems[drinkId] = parsedQuantity;
      }
      
      return {
        ...prev,
        drink_items: newDrinkItems
      };
    });
  }, []);

  const updateSweetQuantity = useCallback((sweetId, quantity) => {
    setOrder(prev => {
      const newSweetItems = { ...prev.sweet_items };
      const parsedQuantity = parseInt(quantity) || 0;
      
      if (parsedQuantity === 0) {
        // Remove item if quantity is 0
        delete newSweetItems[sweetId];
      } else {
        newSweetItems[sweetId] = parsedQuantity;
      }
      
      return {
        ...prev,
        sweet_items: newSweetItems
      };
    });
  }, []);

  const updateCustomCategoryQuantity = useCallback((categoryId, productId, quantity) => {
    setCustomCategoryItems(prev => {
      const categoryItems = { ...(prev[categoryId] || {}) };
      const parsedQuantity = parseInt(quantity, 10) || 0;
      if (parsedQuantity === 0) {
        delete categoryItems[productId];
      } else {
        categoryItems[productId] = parsedQuantity;
      }
      return { ...prev, [categoryId]: categoryItems };
    });
  }, []);

  const startBreakfastOrderEdit = useCallback(() => {
    if (!existingBreakfastOrder) return;
    setBreakfastFormData(existingBreakfastFormValues);
    setIsEditingBreakfastOrder(true);
    editMutationRef.current = null;
    setOrderResetKey(prev => prev + 1);
  }, [existingBreakfastOrder, existingBreakfastFormValues]);

  const cancelBreakfastOrderEdit = useCallback(() => {
    if (existingBreakfastOrder) {
      setBreakfastFormData(existingBreakfastFormValues);
    }
    setIsEditingBreakfastOrder(false);
    editMutationRef.current = null;
    setOrderResetKey(prev => prev + 1);
  }, [existingBreakfastOrder, existingBreakfastFormValues]);

  const submitOrder = useCallback(async () => {
    if (debtOrderLocked || menuMissing || menuError || unavailableTopping || incompleteBreakfast) return;
    // KRITISCHER SCHUTZ: useRef für SOFORTIGEN Check (State ist async!)
    if (isSubmittingRef.current) {
      alert(`⏳ Bitte warten Sie noch ${cooldownRemaining} Sekunden bevor Sie erneut bestellen.`);
      return;
    }
    
    // SOFORT BLOCKIEREN mit REF (synchron, kein React State Delay!)
    // MUST be set BEFORE any async operations or validation!
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    
    // Refresh only at save time; local quantity changes never wait for the network.
    setMenuNotice('');
    try {
      const quantities = {...order.drink_items, ...order.sweet_items, ...customCategoryItems[activeCategory]};
      const terms = (kind, data) => JSON.stringify(kind === 'categories' ? menuCategoryOrderTerms(data, quantities) : data);
      const shown = requiredResources.map(kind => terms(kind, menuData[kind].data));
      const fresh = await menuData.refresh(requiredResources);
      if (activeCategory === 'breakfast' && breakfastFormData?.has_coffee) {
        const response = await axios.get(`${API}/coffee-flat/${currentDepartment.department_id}/${employee.id}`);
        const changed = Boolean(response.data.active) !== Boolean(coffeeFlat?.active);
        setCoffeeFlat(response.data);
        if (changed && !existingBreakfastFormValues?.has_coffee) {
          setMenuNotice('Der Status der Kaffee-Monatsflat hat sich geändert. Bitte prüfe die Summe und speichere erneut.');
          isSubmittingRef.current=false; setIsSubmitting(false); return;
        }
      }
      if (!menuOpenRef.current) return;
      if (menuData.day !== getBerlinDate() || fresh.some((data, index) => terms(requiredResources[index], data) !== shown[index])) {
        setMenuNotice('Preise oder Bestelloptionen wurden aktualisiert. Bitte prüfe die neue Summe und speichere erneut.');
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        return;
      }
    } catch (error) {
      if (!menuOpenRef.current) return;
      setMenuNotice(`Die aktuellen Preise konnten nicht geprüft werden. Deine Auswahl bleibt erhalten. ${priceLoadReason(error)}`);
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      return;
    }

    // TOPPINGS VALIDIERUNG: Prüfe ob Toppings leer sind (verhindert ":" Bug)
    let submittedBreakfastData = breakfastFormData ? { ...breakfastFormData } : null;
    if (activeCategory === 'breakfast' && submittedBreakfastData) {
      const toppings = submittedBreakfastData.toppings || [];
      // Filter out empty strings
      const validToppings = toppings.filter(t => t && t.trim() !== '');
      
      if (validToppings.length === 0 && breakfastFormData.total_halves > 0) {
        alert('⚠️ Bitte wählen Sie mindestens einen Belag für Ihre Brötchen aus.');
        // Reset bei Validierungsfehler
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        return;
      }
      
      // Update breakfastFormData with filtered toppings
      submittedBreakfastData.toppings = validToppings;
    }
    
    try {
      let breakfastItems = [];
      
      // For breakfast category, use form data if available, otherwise use order array
      if (activeCategory === 'breakfast') {
        if (submittedBreakfastData) {
          breakfastItems = [submittedBreakfastData];
        } else if (isEditingBreakfastOrder && existingBreakfastOrder) {
          breakfastItems = [{
            total_halves: 0,
            white_halves: 0,
            seeded_halves: 0,
            toppings: [],
            boiled_eggs: 0,
            fried_eggs: 0,
            scrambled_eggs: 0,
            has_coffee: false,
            has_lunch: false,
            has_dinner: false,
            notes: '',
          }];
        } else if (order.breakfast_items.length > 0) {
          breakfastItems = order.breakfast_items;
        } else {
          alert('Bitte füllen Sie das Frühstücksformular aus.');
          // Reset bei Validierungsfehler
          isSubmittingRef.current = false;
          setIsSubmitting(false);
          return;
        }
      }
      
      // Check if employee already has an order for today (for breakfast only)
      if (activeCategory === 'breakfast') {
        if (isEditingBreakfastOrder && existingBreakfastOrder) {
          const draft = breakfastItems[0];
          const unsignedPayload = buildMealEditPayload(
            existingBreakfastOrder,
            draft,
            'pending-mutation',
          );
          if (Object.keys(unsignedPayload.categories).length === 0) {
            alert('Es wurden keine Änderungen vorgenommen.');
            isSubmittingRef.current = false;
            setIsSubmitting(false);
            return;
          }
          const signature = JSON.stringify({
            expected_revision: unsignedPayload.expected_revision,
            categories: unsignedPayload.categories,
          });
          if (!editMutationRef.current || editMutationRef.current.signature !== signature) {
            const randomPart = globalThis.crypto?.randomUUID
              ? globalThis.crypto.randomUUID()
              : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
            editMutationRef.current = {
              id: `meal-edit-${randomPart}`,
              signature,
            };
          }
          await axios.put(
            `${API}/employee/${employee.id}/orders/${existingBreakfastOrder.id}/meal-categories`,
            {
              ...unsignedPayload,
              mutation_id: editMutationRef.current.id,
            },
            {headers: {'X-Profile-Order-Token': orderToken || ''}},
          );
          editMutationRef.current = null;
          setIsEditingBreakfastOrder(false);
          if (fetchEmployees) await fetchEmployees();
          setSuccessMessage('Bestellung erfolgreich aktualisiert!');
        } else {
          await postOrder({
            employee_id: employee.id,
            department_id: currentDepartment.department_id,
            order_type: activeCategory,
            breakfast_items: breakfastItems,
            drink_items: {},
            sweet_items: {},
            notes: submittedBreakfastData?.notes || null
          });
          setSuccessMessage('Bestellung erfolgreich gespeichert!');
        }
        setShowSuccessNotification(true);
        
        // Reload order data to show locked summary
        await loadExistingOrdersForToday();
        setOrderResetKey(prev => prev + 1);
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      } else {
        if (!selectedMenuCategory) {
          throw new Error('Ausgewählte Kategorie wurde nicht gefunden');
        }
        const selectedItems = selectedMenuCategory.system_key === 'drinks'
          ? order.drink_items
          : selectedMenuCategory.system_key === 'snacks'
            ? order.sweet_items
            : (customCategoryItems[selectedMenuCategory.id] || {});
        const orderData = buildMenuCategoryOrderPayload(
          selectedMenuCategory,
          selectedItems,
          employee.id,
          currentDepartment.department_id
        );

        await postOrder(orderData);
        
        // COOLDOWN STARTEN NACH ERFOLG (verhindert Doppelklick während API-Call & danach)
        setIsSubmitting(true);
        setCooldownRemaining(10);
        
        setSuccessMessage('Bestellung erfolgreich gespeichert!');
        setShowSuccessNotification(true);
        playSucessSound();
        
        // Reset order state after successful order
        setOrder({
          breakfast_items: [],
          drink_items: {},
          sweet_items: {}
        });
        if (!selectedMenuCategory.system_key) {
          setCustomCategoryItems(prev => ({ ...prev, [selectedMenuCategory.id]: {} }));
        }
        setOrderResetKey(prev => prev + 1); // Force remount of order forms
      }
      
      // Note: Employee data refresh and modal closing is now handled by SuccessNotification onClose
      
    } catch (error) {
      console.error('Fehler beim Speichern der Bestellung:', error);
      deadline.refresh();
      
      const detail = error.response?.data?.detail;
      const status = error.response?.status;
      if (status === 403 && typeof detail === 'string' && (detail.includes('Passwort erforderlich') || detail.includes('erneut entsperren'))) {
        onProtectionRequired?.();
      }
      
      // Handle stock-related errors (HTTP 409)
      if (status === 409 && !isEditingBreakfastOrder) {
        alert(`Bestand nicht verfügbar: ${detail || 'Ein Artikel ist nicht mehr auf Lager. Bitte aktualisieren Sie die Seite.'}`);
      } else if (status === 400 && detail) {
        // 400: Show the actual validation error from backend (e.g. empty toppings, mismatched halves,
        // missing variant selection, or duplicate breakfast order).
        alert(`Bestellung ungültig:\n\n${detail}`);
      } else if (status === 403 && detail) {
        alert(detail);
      } else if (status === 423 && detail) {
        alert(detail);
      } else {
        alert(`Fehler beim Speichern der Bestellung${detail ? `:\n\n${detail}` : '. Bitte versuchen Sie es erneut.'}`);
      }

      if (isEditingBreakfastOrder && [403, 409, 423].includes(status)) {
        await loadExistingOrdersForToday();
      }
      
      // Reset bei Fehler - User kann sofort retry
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      setCooldownRemaining(0);
    }
  }, [
    debtOrderLocked, coffeeFlat, existingBreakfastFormValues,
    menuMissing, menuError, menuData, requiredResources, unavailableTopping, incompleteBreakfast,
    deadline.refresh,
    activeCategory,
    breakfastFormData,
    order,
    customCategoryItems,
    selectedMenuCategory,
    employee.id,
    currentDepartment.department_id,
    cooldownRemaining,
    existingBreakfastOrder,
    isEditingBreakfastOrder,
    fetchEmployees,
    setSuccessMessage,
    setShowSuccessNotification,
    setOrder,
    setBreakfastFormData,
    setOrderResetKey,
    setIsSubmitting,
    setCooldownRemaining
  ]);
  
  // Cooldown Timer Effect
  useEffect(() => {
    if (cooldownRemaining > 0) {
      const timer = setTimeout(() => {
        setCooldownRemaining(cooldownRemaining - 1);
        if (cooldownRemaining === 1) {
          isSubmittingRef.current = false;
          setIsSubmitting(false);
        }
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [cooldownRemaining]);

  const [showScrollTop, setShowScrollTop] = useState(false);
  const modalScrollRef = useRef(null);

  // Scroll detection for modal
  useEffect(() => {
    const modalElement = modalScrollRef.current;
    if (!modalElement) return;

    const handleScroll = () => {
      if (modalElement.scrollTop > 200) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    modalElement.addEventListener('scroll', handleScroll);
    return () => modalElement.removeEventListener('scroll', handleScroll);
  }, [debtOrderLocked]);

  const scrollToTop = () => {
    if (modalScrollRef.current) {
      modalScrollRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  return (
    <BalanceOrderGate deadline={deadline} employeeName={employee.name} onClose={handleClose}>
    <div 
      ref={modalScrollRef}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 py-8 overflow-y-auto"
      onClick={handleClose}
    >
      <div 
        className="bg-white rounded-lg max-w-4xl w-full mx-4 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">Bestellung für {employee.name}</h2>
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700 text-xl"
            >
              ×
            </button>
          </div>
          
          {/* Category Tabs - Dynamic based on department config */}
          {departmentConfig && (
            <div className="flex mt-4 gap-1 overflow-x-auto pb-1">
              {[
                ...(departmentConfig.is_breakfast_lunch_enabled
                  ? [{ id: 'breakfast', tabKey: 'breakfast', name: 'Essen' }]
                  : []),
                ...visibleMenuCategories.map(category => ({
                  ...category,
                  tabKey: category.system_key || category.id,
                })),
              ].map((category) => (
                <button
                  key={category.id}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCategoryChange(category.tabKey);
                  }}
                  className={`shrink-0 px-4 py-2 rounded-t-lg ${
                    activeCategory === category.tabKey
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="px-6"><BalanceDeadlineMessage {...deadline} />
          {!deadline.data && !deadline.error && <p className="mt-3 text-sm text-gray-600">Zahlungsstatus wird geprüft …</p>}
        </div>
        <div className="px-6" aria-live="polite">
          {priceIssues.length > 0 && <div role="alert" className="mt-4 rounded-lg bg-amber-50 p-4 text-amber-900">
            <p>Einige Angebote sind derzeit nicht verfügbar. Bitte prüfe die Hinweise zu Preisen und Bestand.</p>
            <ul className="mt-2 list-disc pl-5">{[...new Set(priceIssues)].map(reason => <li key={reason}>{reason}</li>)}</ul>
            <button type="button" onClick={retryMenu} className="mt-2 font-semibold underline">Erneut laden</button>
          </div>}
          {menuNotice && <p role="status" className="mt-4 rounded-lg bg-amber-50 p-4 text-amber-900">{menuNotice}</p>}
          {menuMissing && !renderMissing && !menuError && <p role="status" className="mt-4 text-sm text-gray-600">Aktuelle Tagespreise werden geladen. Deine Auswahl bleibt erhalten.</p>}
          {unavailableTopping && <p role="alert" className="mt-4 rounded-lg bg-amber-50 p-4 text-amber-900">Ein gewählter Belag ist nicht mehr verfügbar. Bitte wähle für die betroffenen Brötchen einen anderen Belag.</p>}
        </div>
        <fieldset disabled={debtOrderLocked || isSubmitting} className="p-6">
          {renderMissing || !departmentConfig || activeCategory === null || isLoadingExistingOrders ? (
            /* Show loading while config is being fetched */
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">{menuError ? 'Bestellung erst nach dem Laden der Preise möglich.' : 'Lade Bestelloptionen und Preise...'}</p>
            </div>
          ) : (
            <>
              {activeCategory === 'breakfast' && departmentConfig.is_breakfast_lunch_enabled && (
                existingBreakfastOrder && !isEditingBreakfastOrder ? (
                  /* SOFT-LOCK: Show read-only summary when order exists */
                  <BreakfastOrderSummary
                    existingOrder={existingBreakfastOrder}
                    breakfastFormData={breakfastFormData}
                    onEdit={startBreakfastOrderEdit}
                    lunchSettings={lunchSettings}
                    todayLunchName={todayLunchName}
                    eggNames={{
                      boiled: lunchSettings.boiled_eggs_name || 'Gekochte Frühstückseier',
                      fried: lunchSettings.fried_eggs_name || 'Spiegeleier',
                      scrambled: lunchSettings.scrambled_eggs_name || 'Rührei'
                    }}
                    toppingLabels={finalToppingLabels}
                  />
                ) : (
              <BreakfastOrderForm
                key={`breakfast-${orderResetKey}`}
                breakfastMenu={breakfastMenu}
                toppingsMenu={toppingsMenu}
                onAddItem={addBreakfastItem}
                rollTypeLabels={rollTypeLabels}
                toppingLabels={finalToppingLabels}
                existingOrderData={isEditingBreakfastOrder ? existingBreakfastFormValues : null}
                boiledEggsPrice={lunchSettings.boiled_eggs_price}
                friedEggsPrice={lunchSettings.fried_eggs_price}
                scrambledEggsPrice={lunchSettings.scrambled_eggs_price}
                coffeePrice={isEditingBreakfastOrder && existingBreakfastFormValues?.has_coffee
                  ? (existingBreakfastFormValues.coffee_unit_price ?? lunchSettings.coffee_price)
                  : coffeeFlat?.active ? 0 : lunchSettings.coffee_price}
                coffeeIncluded={isEditingBreakfastOrder && existingBreakfastFormValues?.has_coffee
                  ? Boolean(existingBreakfastFormValues.coffee_flat_booking_id) : Boolean(coffeeFlat?.active)}
                todayLunchName={todayLunchName}
                eggNames={{
                  boiled: lunchSettings.boiled_eggs_name || 'Gekochte Frühstückseier',
                  fried: lunchSettings.fried_eggs_name || 'Spiegeleier',
                  scrambled: lunchSettings.scrambled_eggs_name || 'Rührei'
                }}
                onDirectSubmit={handleBreakfastFormSubmit}
                availableRollTypes={departmentConfig?.available_roll_types || ['weiss', 'koerner']}
                breakfastItemVisibility={departmentConfig?.breakfast_item_visibility}
                lunchPrice={todayLunchPrice}
                lunchVariants={lunchVariants}
                dinnerName={todayDinnerName}
                dinnerPrice={todayDinnerPrice}
                dinnerVariants={dinnerVariants}
                dinnerEnabled={departmentConfig?.meal_types?.dinner?.enabled !== false}
                breakfastOpen={!mealOrderingStatus.meals?.breakfast?.is_closed}
                lunchOpen={!mealOrderingStatus.meals?.lunch?.is_closed}
                dinnerOpen={!mealOrderingStatus.meals?.dinner?.is_closed}
                mealStatuses={mealOrderingStatus.meals}
                mealEditability={existingBreakfastOrder?.meal_editability || {}}
                isEditing={isEditingBreakfastOrder}
                nightOnly={nightOnly}
                preservedOrderTotal={isEditingBreakfastOrder && isButterOnlyEdit(existingBreakfastOrder, breakfastFormData)
                  ? Math.abs(Number(existingBreakfastOrder.total_price)) : null}
                pricesPending={menuMissing}
                priceIssues={priceAvailability.issues}
              />
            )
          )}

          {selectedMenuCategory && activeCategory !== 'breakfast' && (
            <DrinksOrderForm
              key={`${selectedMenuCategory.id}-${orderResetKey}`}
              drinksMenu={Array.isArray(selectedMenuCategory.products) ? selectedMenuCategory.products.filter(item => item && typeof item === 'object') : []}
              title={selectedMenuCategory.name}
              quantities={selectedMenuCategory.system_key === 'drinks'
                ? order.drink_items
                : selectedMenuCategory.system_key === 'snacks'
                  ? order.sweet_items
                  : (customCategoryItems[selectedMenuCategory.id] || {})}
              onUpdateQuantity={(productId, quantity) => {
                if (selectedMenuCategory.system_key === 'drinks') {
                  updateDrinkQuantity(productId, quantity);
                } else if (selectedMenuCategory.system_key === 'snacks') {
                  updateSweetQuantity(productId, quantity);
                } else {
                  updateCustomCategoryQuantity(selectedMenuCategory.id, productId, quantity);
                }
              }}
            />
          )}

          {departmentConfig && activeCategory !== null && !(activeCategory === 'breakfast' && existingBreakfastOrder && !isEditingBreakfastOrder) && (
            <div className="mt-6 flex justify-end space-x-4">
              <button
                type="button"
                onClick={isEditingBreakfastOrder ? cancelBreakfastOrderEdit : handleClose}
                className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={submitOrder}
                disabled={menuError || menuMissing || unavailableTopping || incompleteBreakfast || isSubmittingRef.current || isSubmitting}
                className={`px-6 py-2 rounded-lg text-white font-medium transition-colors ${
                  (menuError || menuMissing || unavailableTopping || incompleteBreakfast || isSubmittingRef.current || isSubmitting)
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {(isSubmittingRef.current || isSubmitting)
                  ? (cooldownRemaining > 0 ? `⏳ Bitte warten (${cooldownRemaining}s)` : 'Bestellung wird geprüft …')
                  : isEditingBreakfastOrder ? 'Änderungen speichern' : 'Bestellung speichern'}
              </button>
            </div>
          )}
          </>
          )}
        </fieldset>
      </div>

      {/* Success Notification */}
      {showSuccessNotification && (
        <SuccessNotification
          message={successMessage}
          onClose={() => {
            setShowSuccessNotification(false);
            const wasReset = successMessage.includes('zurückgesetzt');
            setSuccessMessage('');
            
            // Refresh employee data
            if (fetchEmployees) {
              fetchEmployees();
            }
            
            if (!wasReset) {
              // Normal order: close modal
              if (onOrderComplete) {
                onOrderComplete();
              }
              handleClose();
            }
            // After reset: keep modal open so user can re-order
          }}
        />
      )}
    </div>
    </BalanceOrderGate>
  );
};


// Read-only summary of existing breakfast order (soft-lock view)
const BreakfastOrderSummary = ({ existingOrder, breakfastFormData, onEdit, lunchSettings, todayLunchName, eggNames, toppingLabels }) => {
  if (!breakfastFormData) return null;
  
  const item = breakfastFormData;
  const whiteHalves = item.white_halves || 0;
  const seededHalves = item.seeded_halves || 0;
  const totalHalves = whiteHalves + seededHalves;
  const toppings = item.toppings || [];
  
  // Build topping display per roll
  const rollDetails = [];
  let toppingIndex = 0;
  for (let i = 0; i < whiteHalves; i++) {
    const topping = toppings[toppingIndex] || '';
    const toppingName = toppingLabels[topping] || topping || 'Ohne Belag';
    rollDetails.push({ type: 'Helles Brötchen', topping: toppingName, withoutButter: Boolean(item.without_butter?.[toppingIndex]) });
    toppingIndex++;
  }
  for (let i = 0; i < seededHalves; i++) {
    const topping = toppings[toppingIndex] || '';
    const toppingName = toppingLabels[topping] || topping || 'Ohne Belag';
    rollDetails.push({ type: 'Körnerbrötchen', topping: toppingName, withoutButter: Boolean(item.without_butter?.[toppingIndex]) });
    toppingIndex++;
  }

  const totalPrice = existingOrder?.total_price || 0;

  return (
    <div className="space-y-5" data-testid="breakfast-order-summary">
      {/* Header */}
      <div className="text-center pb-2">
        <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-full mb-3">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="font-semibold text-sm">Bestellung abgegeben</span>
        </div>
        <h3 className="text-xl font-bold text-gray-800">Deine heutige Bestellung</h3>
        <p className="text-sm text-gray-500 mt-1">Die Bestellung ist gespeichert und kann je Mahlzeit bearbeitet werden.</p>
      </div>

      {/* Order Details */}
      <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-2xl p-5 shadow-sm border border-gray-200">
        <div className="space-y-3">
          {/* Rolls with toppings */}
          {rollDetails.length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <span className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center text-white text-xs font-bold">B</span>
                Brötchen ({totalHalves} Hälften)
              </h4>
              <div className="space-y-1 ml-8">
                {rollDetails.map((roll, idx) => (
                  <div key={idx} className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                    <span className="text-gray-400">-</span>
                    <span>{roll.type}</span>
                    <span className="text-gray-400">mit</span>
                    <span className="font-medium text-gray-700">{roll.topping}</span>
                    {roll.withoutButter && <span className="text-gray-600">· ohne Butter</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Extras */}
          {(item.boiled_eggs > 0 || item.fried_eggs > 0 || item.scrambled_eggs > 0 || item.has_coffee) && (
            <div>
              <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <span className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center text-white text-xs font-bold">+</span>
                Extras
              </h4>
              <div className="space-y-1 ml-8">
                {item.boiled_eggs > 0 && (
                  <div className="text-sm text-gray-600">- {item.boiled_eggs}x {eggNames.boiled}</div>
                )}
                {item.fried_eggs > 0 && (
                  <div className="text-sm text-gray-600">- {item.fried_eggs}x {eggNames.fried}</div>
                )}
                {item.scrambled_eggs > 0 && (
                  <div className="text-sm text-gray-600">- {item.scrambled_eggs}x {eggNames.scrambled}</div>
                )}
                {item.has_coffee && (
                  <div className="text-sm text-gray-600">- Kaffee-Flatrate</div>
                )}
              </div>
            </div>
          )}

          {/* Lunch */}
          {item.has_lunch && (() => {
            // Show variant details: mixed selections > single variant name > todayLunchName fallback
            const selections = existingOrder?.lunch_variant_selections || [];
            const lunchCount = Math.max(1, parseInt(item.lunch_count || existingOrder?.lunch_count || 1, 10));
            const variantName = existingOrder?.lunch_variant_name || todayLunchName || 'Mittagessen';
            return (
              <div>
                <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <span className="w-6 h-6 bg-violet-500 rounded-full flex items-center justify-center text-white text-xs font-bold">M</span>
                  Mittagessen
                </h4>
                <div className="ml-8 text-sm text-gray-600 space-y-0.5">
                  {selections.length > 0 ? (
                    selections
                      .filter(sel => (parseInt(sel.count, 10) || 0) > 0)
                      .map((sel, idx) => (
                        <div key={idx}>- {sel.count}x {sel.variant_name || 'Variante'}</div>
                      ))
                  ) : (
                    <div>- {lunchCount > 1 ? `${lunchCount}x ` : ''}{variantName}</div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Dinner */}
          {item.has_dinner && (() => {
            const selections = existingOrder?.dinner_variant_selections || [];
            const dinnerCount = Math.max(1, parseInt(item.dinner_count || existingOrder?.dinner_count || 1, 10));
            const variantName = existingOrder?.dinner_variant_name || 'Abendessen';
            return (
              <div>
                <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <span className="w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-bold">A</span>
                  Abendessen
                </h4>
                <div className="ml-8 text-sm text-gray-600 space-y-0.5">
                  {selections.length > 0 ? (
                    selections
                      .filter(sel => (parseInt(sel.count, 10) || 0) > 0)
                      .map((sel, idx) => (
                        <div key={idx}>- {sel.count}x {sel.variant_name || 'Variante'}</div>
                      ))
                  ) : (
                    <div>- {dinnerCount > 1 ? `${dinnerCount}x ` : ''}{variantName}</div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Notes */}
          {item.notes && (
            <div>
              <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <span className="w-6 h-6 bg-gray-400 rounded-full flex items-center justify-center text-white text-xs font-bold">N</span>
                Sonderwünsche
              </h4>
              <div className="ml-8 text-sm text-gray-600 italic">"{item.notes}"</div>
            </div>
          )}
        </div>

        {/* Total */}
        <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
          <span className="font-bold text-gray-800">Gesamtkosten</span>
          <span className="font-bold text-lg text-red-600">-{totalPrice.toFixed(2)} €</span>
        </div>
      </div>

      {/* Edit Button */}
      <div className="flex flex-col items-center gap-3">
        <button
          type="button"
          onClick={onEdit}
          data-testid="edit-breakfast-order-btn"
          className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-medium transition-all shadow-sm hover:shadow-md flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Bestellung ändern
        </button>
        <p className="text-xs text-gray-400 text-center">
          Nur tatsächlich geänderte Mahlzeiten werden neu berechnet.
        </p>
      </div>
    </div>
  );
};


// Simplified Breakfast Order Form with Memo to prevent unnecessary re-renders - Direct Roll Selection with Topping Assignment
const BreakfastOrderForm = ({ breakfastMenu, toppingsMenu, onAddItem, rollTypeLabels, toppingLabels, onDirectSubmit, existingOrderData, boiledEggsPrice, friedEggsPrice, scrambledEggsPrice, coffeePrice, todayLunchName, eggNames, availableRollTypes = ['weiss', 'koerner'], breakfastItemVisibility, lunchPrice = 0, lunchVariants = [], dinnerName = '', dinnerPrice = 0, dinnerVariants = [], dinnerEnabled = false, breakfastOpen = true, lunchOpen = true, dinnerOpen = true, mealStatuses = {}, mealEditability = {}, isEditing = false, nightOnly = false, pricesPending = false, priceIssues = {}, preservedOrderTotal = null, coffeeIncluded = false }) => {
  const [whiteRolls, setWhiteRolls] = useState(0);
  const [seededRolls, setSeededRolls] = useState(0);
  const [toppingAssignments, setToppingAssignments] = useState([]);
  const [defaultWithoutButter, setDefaultWithoutButter] = useState(false);
  const bulkButterRef = useRef(null);
  const withoutButterCount = toppingAssignments.filter(assignment => assignment.withoutButter).length;
  useEffect(() => {
    if (bulkButterRef.current) bulkButterRef.current.indeterminate = withoutButterCount > 0 && withoutButterCount < toppingAssignments.length;
  }, [withoutButterCount, toppingAssignments.length]);
  const [hasLunch, setHasLunch] = useState(false);
  const [boiledEggs, setBoiledEggs] = useState(0);
  const [friedEggs, setFriedEggs] = useState(0);
  const [scrambledEggs, setScrambledEggs] = useState(0);
  const [hasCoffee, setHasCoffee] = useState(false);
  const [hasDinner, setHasDinner] = useState(false);
  const [notes, setNotes] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  // Phase 2: variant selection + counts
  const [lunchVariantId, setLunchVariantId] = useState(null);
  const [dinnerVariantId, setDinnerVariantId] = useState(null);
  const [lunchCount, setLunchCount] = useState(1);
  const [dinnerCount, setDinnerCount] = useState(1);
  // Phase 4: mixed variant selections (variant_id -> count)
  const [lunchCounts, setLunchCounts] = useState({}); // e.g. {v1: 2, v2: 1}
  const [dinnerCounts, setDinnerCounts] = useState({});
  const mealVisibility = getNightOnlyMealVisibility(nightOnly);
  const visibleBreakfastItems = {
    boiled_eggs: true,
    fried_eggs: true,
    scrambled_eggs: true,
    coffee: true,
    ...(breakfastItemVisibility || {}),
  };
  const visibleExtrasCount = [
    visibleBreakfastItems.boiled_eggs,
    visibleBreakfastItems.fried_eggs,
    visibleBreakfastItems.scrambled_eggs,
  ].filter(Boolean).length;
  const hasVisibleExtras = visibleExtrasCount > 0 || visibleBreakfastItems.coffee;
  const protectionNotice = (mealType, label) => {
    if (!isEditing || mealEditability?.[mealType]?.editable !== false) return null;
    const reason = mealEditability[mealType].reason;
    if (reason === 'paid') return `${label} nach Zahlung nicht mehr änderbar`;
    if (reason === 'sponsored') return `Dieses ${label} wurde gesponsert`;
    return `${label} kann nicht mehr geändert werden`;
  };
  const breakfastLockNotice = mealLockNotice('Frühstück', mealStatuses.breakfast)
    || protectionNotice('breakfast', 'Frühstück');
  const lunchLockNotice = mealLockNotice('Mittagessen', mealStatuses.lunch)
    || protectionNotice('lunch', 'Mittagessen');
  const dinnerLockNotice = mealLockNotice('Abendessen', mealStatuses.dinner)
    || protectionNotice('dinner', 'Abendessen');
  const breakfastDisabled = isEditing && (!breakfastOpen || mealEditability?.breakfast?.editable === false);
  const lunchDisabled = Boolean(priceIssues.lunch) || (isEditing && (!lunchOpen || mealEditability?.lunch?.editable === false));
  const dinnerDisabled = Boolean(priceIssues.dinner) || (isEditing && (!dinnerOpen || mealEditability?.dinner?.editable === false));

  useEffect(() => {
    if (!visibleBreakfastItems.boiled_eggs) setBoiledEggs(0);
    if (!visibleBreakfastItems.fried_eggs) setFriedEggs(0);
    if (!visibleBreakfastItems.scrambled_eggs) setScrambledEggs(0);
    if (!visibleBreakfastItems.coffee) setHasCoffee(false);
  }, [
    visibleBreakfastItems.boiled_eggs,
    visibleBreakfastItems.fried_eggs,
    visibleBreakfastItems.scrambled_eggs,
    visibleBreakfastItems.coffee,
  ]);

  useEffect(() => {
    if (!breakfastOpen && !isEditing) {
      setWhiteRolls(0);
      setSeededRolls(0);
      setToppingAssignments([]);
      setBoiledEggs(0);
      setFriedEggs(0);
      setScrambledEggs(0);
      setHasCoffee(false);
    }
    if (!lunchOpen && !isEditing) {
      setHasLunch(false);
      setLunchCounts({});
    }
    if (!dinnerOpen && !isEditing) {
      setHasDinner(false);
      setDinnerCounts({});
    }
  }, [breakfastOpen, lunchOpen, dinnerOpen, isEditing]);

  // Carry quantities across changes between single and mixed variant selection.
  const previousLunchVariants = useRef(lunchVariants);
  useEffect(() => {
    const previous = previousLunchVariants.current;
    previousLunchVariants.current = lunchVariants;
    if (isEditing && existingOrderData && previous === lunchVariants) return;
    if (lunchVariants.length === 1) {
      const id = lunchVariants[0].id;
      setLunchVariantId(id);
      if (previous.length >= 2) setLunchCount(lunchCounts[id] || 1);
    } else if (lunchVariants.length === 0) {
      setLunchVariantId(null);
      if (previous.length >= 2) setLunchCount(Object.values(lunchCounts).reduce((sum, count) => sum + count, 0) || 1);
    }
    if (previous.length === 1 && lunchVariants.length >= 2) {
      const id = previous[0].id;
      setLunchCounts(lunchVariants.some(variant => variant.id === id) ? {[id]: lunchCount} : {});
    } else {
      setLunchCounts(counts => Object.fromEntries(Object.entries(counts).filter(([id]) => lunchVariants.some(variant => variant.id === id))));
    }
  }, [lunchVariants, existingOrderData, isEditing]);
  const previousDinnerVariants = useRef(dinnerVariants);
  useEffect(() => {
    const previous = previousDinnerVariants.current;
    previousDinnerVariants.current = dinnerVariants;
    if (isEditing && existingOrderData && previous === dinnerVariants) return;
    if (dinnerVariants.length === 1) {
      const id = dinnerVariants[0].id;
      setDinnerVariantId(id);
      if (previous.length >= 2) setDinnerCount(dinnerCounts[id] || 1);
    } else if (dinnerVariants.length === 0) {
      setDinnerVariantId(null);
      if (previous.length >= 2) setDinnerCount(Object.values(dinnerCounts).reduce((sum, count) => sum + count, 0) || 1);
    }
    if (previous.length === 1 && dinnerVariants.length >= 2) {
      const id = previous[0].id;
      setDinnerCounts(dinnerVariants.some(variant => variant.id === id) ? {[id]: dinnerCount} : {});
    } else {
      setDinnerCounts(counts => Object.fromEntries(Object.entries(counts).filter(([id]) => dinnerVariants.some(variant => variant.id === id))));
    }
  }, [dinnerVariants, existingOrderData, isEditing]);

  const selectedLunchVariant = lunchVariants.find(v => v.id === lunchVariantId) || null;
  const selectedDinnerVariant = dinnerVariants.find(v => v.id === dinnerVariantId) || null;

  // Phase 4: derived totals for mixed selection UI (only when ≥2 variants)
  const lunchMixMode = lunchVariants.length >= 2;
  const dinnerMixMode = dinnerVariants.length >= 2;
  const lunchMixTotal = Object.values(lunchCounts).reduce((s, n) => s + (parseInt(n) || 0), 0);
  const dinnerMixTotal = Object.values(dinnerCounts).reduce((s, n) => s + (parseInt(n) || 0), 0);
  const lunchMixCost = lunchVariants.reduce((s, v) => s + (v.price || 0) * (parseInt(lunchCounts[v.id]) || 0), 0);
  const dinnerMixCost = dinnerVariants.reduce((s, v) => s + (v.price || 0) * (parseInt(dinnerCounts[v.id]) || 0), 0);

  const setLunchCountFor = (variantId, delta) => {
    setLunchCounts(prev => {
      const cur = parseInt(prev[variantId]) || 0;
      const next = Math.max(0, Math.min(20, cur + delta));
      return { ...prev, [variantId]: next };
    });
  };
  const setDinnerCountFor = (variantId, delta) => {
    setDinnerCounts(prev => {
      const cur = parseInt(prev[variantId]) || 0;
      const next = Math.max(0, Math.min(20, cur + delta));
      return { ...prev, [variantId]: next };
    });
  };

  // Initialize form with existing order data if available (only once)
  useEffect(() => {
    if (existingOrderData && Object.keys(existingOrderData).length > 0 && !isInitialized) {
      setWhiteRolls(existingOrderData.white_halves || 0);
      setSeededRolls(existingOrderData.seeded_halves || 0);
      setHasLunch(existingOrderData.has_lunch || false);
      setHasDinner(existingOrderData.has_dinner || false);
      setBoiledEggs(existingOrderData.boiled_eggs || 0);
      setFriedEggs(existingOrderData.fried_eggs || 0);
      setScrambledEggs(existingOrderData.scrambled_eggs || 0);
      setHasCoffee(existingOrderData.has_coffee || false);
      setNotes(existingOrderData.notes || '');
      setLunchVariantId(existingOrderData.lunch_variant_id || null);
      setDinnerVariantId(existingOrderData.dinner_variant_id || null);
      setLunchCount(existingOrderData.lunch_count || 1);
      setDinnerCount(existingOrderData.dinner_count || 1);
      setLunchCounts(existingVariantCounts(existingOrderData, lunchVariants, 'lunch'));
      setDinnerCounts(existingVariantCounts(existingOrderData, dinnerVariants, 'dinner'));
      
      // Reconstruct toppings assignments
      if (existingOrderData.toppings && Array.isArray(existingOrderData.toppings)) {
        const newAssignments = [];
        let whiteIndex = 1;
        let seededIndex = 1;
        
        existingOrderData.toppings.forEach((topping, index) => {
          const rollType = index < (existingOrderData.white_halves || 0) ? 'weiss' : 'koerner';
          let rollLabel;
          
          if (rollType === 'weiss') {
            rollLabel = `Helles Brötchen ${whiteIndex}`;  // Use consistent simple naming
            whiteIndex++;
          } else {
            rollLabel = `Körnerbrötchen ${seededIndex}`;  // Use consistent simple naming
            seededIndex++;
          }
          
          newAssignments.push({
            id: `existing_${index}`,
            rollType: rollType,
            rollLabel: rollLabel,
            topping: topping,
            withoutButter: Boolean(existingOrderData.without_butter?.[index])
          });
        });
        setToppingAssignments(newAssignments);
      }
      setIsInitialized(true);
    } else if (!existingOrderData || Object.keys(existingOrderData).length === 0) {
      // Allow re-initialization if existingOrderData becomes empty (form reset)
      setIsInitialized(false);
    }
  }, [existingOrderData, isInitialized]);

  // Get actual prices from menu (use admin-set prices directly)
  const getBreakfastPrice = (rollType) => {
    const menuItem = breakfastMenu.find(item => item.roll_type === rollType);
    return validPrice(menuItem?.price) ? menuItem.price : 0;
  };

  const whiteRollPrice = getBreakfastPrice('weiss');
  const seededRollPrice = getBreakfastPrice('koerner');

  const totalHalves = whiteRolls + seededRolls;
  
  // Get eggs prices from props (passed from lunch settings)
  const boiledEggsCost = boiledEggs * (validPrice(boiledEggsPrice) ? boiledEggsPrice : 0);
  const friedEggsCost = friedEggs * (validPrice(friedEggsPrice) ? friedEggsPrice : 0);
  const scrambledEggsCost = scrambledEggs * (validPrice(scrambledEggsPrice) ? scrambledEggsPrice : 0);
  const coffeeCost = hasCoffee && validPrice(coffeePrice) ? coffeePrice : 0;
  
  // Calculate separate cost components for better display
  const rollsCost = (whiteRolls * whiteRollPrice) + (seededRolls * seededRollPrice);
  
  // Calculate toppings cost
  const toppingsCost = toppingAssignments.reduce((sum, assignment) => {
    if (!assignment.topping) return sum;
    const toppingItem = toppingsMenu.find(t => (t.topping_type || t.id) === assignment.topping);
    return sum + (finitePrice(toppingItem?.price) ? toppingItem.price : 0);
  }, 0);
  const unavailableTopping = toppingAssignments.some(assignment => assignment.topping
    && !toppingsMenu.some(item => (item.topping_type || item.id) === assignment.topping));
  
  // Get lunch price from daily endpoint (passed via prop or fetched)
  const effectiveLunchPrice = selectedLunchVariant ? selectedLunchVariant.price : (lunchPrice || 0);
  const effectiveDinnerPrice = selectedDinnerVariant ? selectedDinnerVariant.price : (dinnerPrice || 0);
  // Phase 4: in mix-mode use per-variant totals, otherwise single variant × count
  const lunchCost = hasLunch ? (lunchMixMode ? lunchMixCost : effectiveLunchPrice * (lunchCount || 1)) : 0;
  const dinnerCost = hasDinner ? (dinnerMixMode ? dinnerMixCost : effectiveDinnerPrice * (dinnerCount || 1)) : 0;
  
  const totalCost = rollsCost + toppingsCost + boiledEggsCost + friedEggsCost + scrambledEggsCost + coffeeCost + lunchCost + dinnerCost;

  // Event handlers - defined as stable functions with useCallback
  const handleLunchChange = useCallback((e) => {
    setHasLunch(e.target.checked);
  }, []);

  const handleCoffeeChange = useCallback((e) => {
    setHasCoffee(e.target.checked);
  }, []);
  
  const handleWhiteRollsChange = useCallback((value) => {
    setWhiteRolls(value);
  }, []);
  
  const handleSeededRollsChange = useCallback((value) => {
    setSeededRolls(value);
  }, []);
  
  const handleBoiledEggsChange = useCallback((value) => {
    setBoiledEggs(value);
  }, []);
  
  const handleFriedEggsChange = useCallback((value) => {
    setFriedEggs(value);
  }, []);
  
  const handleScrambledEggsChange = useCallback((value) => {
    setScrambledEggs(value);
  }, []);

  // Update topping assignments when roll counts change
  // CRITICAL FIX: Only update assignments when user manually changes roll counts
  // Don't reset during initialization phase WHEN editing an existing order
  const existingOrderDataRef = useRef(existingOrderData);
  
  // Update ref when existingOrderData changes, but don't trigger re-render
  useEffect(() => {
    existingOrderDataRef.current = existingOrderData;
  }, [existingOrderData]);
  
  useEffect(() => {
    // Only skip this effect if we're currently initializing an existing order
    // (i.e., existingOrderData exists but not yet initialized)
    const isCurrentlyInitializing = existingOrderDataRef.current && 
                                    Object.keys(existingOrderDataRef.current).length > 0 && 
                                    !isInitialized;
    
    if (isCurrentlyInitializing) {
      return; // Let the initialization useEffect handle it for existing orders
    }
    
    const newAssignments = [];
    const oldAssignmentsByLabel = {};
    
    // Create lookup of existing assignments by label to preserve selections
    toppingAssignments.forEach(assignment => {
      oldAssignmentsByLabel[assignment.rollLabel] = assignment;
    });
    
    // Add white roll topping slots using menu names with fallbacks
    for (let i = 0; i < whiteRolls; i++) {
      const rollLabel = `Helles Brötchen ${i + 1}`;  // Use consistent simple naming
      newAssignments.push({
        id: `white_${i}`,
        rollType: 'weiss',
        rollLabel: rollLabel,
        topping: oldAssignmentsByLabel[rollLabel]?.topping || '',
        withoutButter: oldAssignmentsByLabel[rollLabel]?.withoutButter ?? defaultWithoutButter
      });
    }
    // Add seeded roll topping slots using menu names with fallbacks
    for (let i = 0; i < seededRolls; i++) {
      const rollLabel = `Körnerbrötchen ${i + 1}`;  // Use consistent simple naming
      newAssignments.push({
        id: `seeded_${i}`,
        rollType: 'koerner',
        rollLabel: rollLabel,
        topping: oldAssignmentsByLabel[rollLabel]?.topping || '',
        withoutButter: oldAssignmentsByLabel[rollLabel]?.withoutButter ?? defaultWithoutButter
      });
    }
    setToppingAssignments(newAssignments);
  }, [whiteRolls, seededRolls, isInitialized]); // Removed existingOrderData from dependencies - use ref instead!

  // Update form data when any valid combination is selected
  // OPTIMIZED: Use useMemo to prevent unnecessary recalculations
  const breakfastData = useMemo(() => {
    const hasRolls = totalHalves > 0;
    const hasValidRollOrder = hasRolls && toppingAssignments.length === totalHalves && 
                              !toppingAssignments.some(a => !a.topping);
    const hasExtras = boiledEggs > 0 || friedEggs > 0 || scrambledEggs > 0 || hasLunch || hasCoffee || hasDinner;
    
    // Allow submission if user has valid order
    if (hasValidRollOrder || (hasExtras && !hasRolls)) {
      const toppings = hasRolls ? toppingAssignments.map(assignment => assignment.topping) : [];
      // Phase 4: Build variant selections if in mix-mode
      const lunchSelections = (hasLunch && lunchMixMode)
        ? Object.entries(lunchCounts).filter(([, c]) => (parseInt(c) || 0) > 0).map(([vid, c]) => ({ variant_id: vid, count: parseInt(c) || 0 }))
        : null;
      const dinnerSelections = (hasDinner && dinnerMixMode)
        ? Object.entries(dinnerCounts).filter(([, c]) => (parseInt(c) || 0) > 0).map(([vid, c]) => ({ variant_id: vid, count: parseInt(c) || 0 }))
        : null;
      return {
        total_halves: totalHalves,
        white_halves: whiteRolls,
        seeded_halves: seededRolls,
        toppings: toppings,
        without_butter: toppingAssignments.map(assignment => Boolean(assignment.withoutButter)),
        has_lunch: hasLunch,
        boiled_eggs: boiledEggs,
        fried_eggs: friedEggs,
        scrambled_eggs: scrambledEggs,
        has_coffee: hasCoffee,
        has_dinner: hasDinner,
        lunch_variant_id: hasLunch && !lunchMixMode ? lunchVariantId : null,
        dinner_variant_id: hasDinner && !dinnerMixMode ? dinnerVariantId : null,
        lunch_count: hasLunch ? (lunchMixMode ? Math.max(1, lunchMixTotal) : (lunchCount || 1)) : 1,
        dinner_count: hasDinner ? (dinnerMixMode ? Math.max(1, dinnerMixTotal) : (dinnerCount || 1)) : 1,
        lunch_variant_selections: lunchSelections,
        dinner_variant_selections: dinnerSelections,
        notes: notes
      };
    } else if (totalHalves === 0 && !hasExtras) {
      return null;
    }
    return undefined; // Not ready yet
  }, [toppingAssignments, totalHalves, hasLunch, boiledEggs, friedEggs, scrambledEggs, hasCoffee, hasDinner, lunchVariantId, dinnerVariantId, lunchCount, dinnerCount, lunchCounts, dinnerCounts, lunchMixMode, dinnerMixMode, lunchMixTotal, dinnerMixTotal, notes, whiteRolls, seededRolls]);

  // Only call onDirectSubmit when breakfastData actually changes
  useEffect(() => {
    if (onDirectSubmit) {
      onDirectSubmit(breakfastData);
    }
  }, [breakfastData]); // Only depend on the memoized data, not individual fields

  const handleToppingAssignment = useCallback((assignmentIndex, toppingType) => {
    setToppingAssignments(prev => {
      const newAssignments = [...prev];
      newAssignments[assignmentIndex] = {
        ...newAssignments[assignmentIndex],
        topping: toppingType
      };
      return newAssignments;
    });
  }, []);

  const handleAddItem = () => {
    const hasRolls = totalHalves > 0;
    const hasEggsOrLunch = boiledEggs > 0 || friedEggs > 0 || scrambledEggs > 0 || hasLunch;
    
    // Validate that user has selected something
    if (!hasRolls && !hasEggsOrLunch) {
      alert('Bitte wählen Sie mindestens ein Brötchen, Frühstückseier oder Mittagessen.');
      return;
    }

    // If user selected rolls, check if all toppings are assigned  
    if (hasRolls) {
      const unassignedCount = toppingAssignments.filter(assignment => !assignment.topping).length;
      if (unassignedCount > 0) {
        alert(`Bitte weisen Sie allen ${totalHalves} Brötchenhälften einen Belag zu. ${unassignedCount} fehlen noch.`);
        return;
      }
    }

    // Convert assignments to the expected format (empty array if no rolls)
    const toppings = hasRolls ? toppingAssignments.map(assignment => assignment.topping) : [];
    
    onAddItem(totalHalves, whiteRolls, seededRolls, toppings, hasLunch, boiledEggs, friedEggs, scrambledEggs, totalCost);
    
    // Reset form
    setWhiteRolls(0);
    setSeededRolls(0);
    setToppingAssignments([]);
    setHasLunch(false);
    setBoiledEggs(0);
    setFriedEggs(0);
    setScrambledEggs(0);
    setNotes('');
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="text-center pb-2">
        <h3 className="text-xl font-bold text-gray-800">Essensbestellung</h3>
        <p className="text-sm text-gray-500 mt-1">
          {nightOnly ? 'Abendessen' : `Frühstück, Mittagessen${dinnerEnabled ? ', Abendessen' : ''} & Extras`}
        </p>
      </div>

      <>
      {!isEditing && ((priceIssues.weiss && whiteRolls > 0) || (priceIssues.koerner && seededRolls > 0)
        || (priceIssues.boiled_eggs && boiledEggs > 0) || (priceIssues.fried_eggs && friedEggs > 0)
        || (priceIssues.scrambled_eggs && scrambledEggs > 0) || (priceIssues.coffee && hasCoffee)
        || (priceIssues.lunch && hasLunch) || (priceIssues.dinner && hasDinner)) && (
        <button type="button" className="text-sm font-semibold underline" onClick={() => {
          if (priceIssues.weiss) setWhiteRolls(0);
          if (priceIssues.koerner) setSeededRolls(0);
          if (priceIssues.boiled_eggs) setBoiledEggs(0);
          if (priceIssues.fried_eggs) setFriedEggs(0);
          if (priceIssues.scrambled_eggs) setScrambledEggs(0);
          if (priceIssues.coffee) setHasCoffee(false);
          if (priceIssues.lunch) { setHasLunch(false); setLunchCounts({}); }
          if (priceIssues.dinner) { setHasDinner(false); setDinnerCounts({}); }
        }}>Nicht verfügbare Auswahl entfernen</button>
      )}
      {mealVisibility.breakfast && breakfastLockNotice && (
        <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
          {breakfastLockNotice}
        </p>
      )}

      {mealVisibility.breakfast && (breakfastOpen || isEditing) && (
      <fieldset disabled={breakfastDisabled} className={breakfastDisabled ? 'space-y-5 opacity-70' : 'space-y-5'}>
      {/* Step 1: Select Roll Counts */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-5 shadow-sm border border-amber-100">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold text-sm">1</div>
          <h4 className="font-semibold text-gray-800">Brötchen wählen</h4>
        </div>
        
        <div className={`grid grid-cols-1 ${availableRollTypes.length > 1 ? 'sm:grid-cols-2' : ''} gap-4`}>
          {/* Helle Brötchen */}
          {availableRollTypes.includes('weiss') && (
          <fieldset disabled={Boolean(priceIssues.weiss)} className="bg-white rounded-xl p-4 shadow-sm border border-amber-100">
            <div className="flex items-center justify-between mb-3">
              <span className="font-medium text-gray-700">Helle Brötchen</span>
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                {priceIssues.weiss ? '—' : whiteRollPrice.toFixed(2)} €/Hälfte
              </span>
            </div>
            <NumberSelector
              value={whiteRolls}
              onChange={handleWhiteRollsChange}
              min={0}
              max={20}
              unit="Hälften"
            />
            {whiteRolls > 0 && (
              <div className="mt-2 text-right text-sm font-medium text-amber-600">
                = {(whiteRolls * whiteRollPrice).toFixed(2)} €
              </div>
            )}
          </fieldset>
          )}
          
          {/* Körner Brötchen */}
          {availableRollTypes.includes('koerner') && (
          <fieldset disabled={Boolean(priceIssues.koerner)} className="bg-white rounded-xl p-4 shadow-sm border border-amber-100">
            <div className="flex items-center justify-between mb-3">
              <span className="font-medium text-gray-700">Körnerbrötchen</span>
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                {priceIssues.koerner ? '—' : seededRollPrice.toFixed(2)} €/Hälfte
              </span>
            </div>
            <NumberSelector
              value={seededRolls}
              onChange={handleSeededRollsChange}
              min={0}
              max={20}
              unit="Hälften"
            />
            {seededRolls > 0 && (
              <div className="mt-2 text-right text-sm font-medium text-amber-600">
                = {(seededRolls * seededRollPrice).toFixed(2)} €
              </div>
            )}
          </fieldset>
          )}
        </div>
      </div>

      {/* Step 2: Assign Toppings to Each Roll */}
      {totalHalves > 0 && (
        <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-5 shadow-sm border border-emerald-100">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-sm">2</div>
            <h4 className="font-semibold text-gray-800">Beläge zuweisen</h4>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Wähle für jede Brötchenhälfte einen Belag
          </p>
          
          <label className="mb-3 flex min-h-11 items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" ref={bulkButterRef} data-testid="without-butter-all"
              checked={toppingAssignments.length > 0 && withoutButterCount === toppingAssignments.length}
              onChange={event => {
                const withoutButter = event.target.checked;
                setDefaultWithoutButter(withoutButter);
                setToppingAssignments(previous => previous.map(assignment => ({...assignment, withoutButter})));
              }} className="h-4 w-4 shrink-0 accent-gray-700" />
            Alle ohne Butter/Margarine
          </label>
          <div className="space-y-2">
            {toppingAssignments.map((assignment, index) => (
              <div 
                key={assignment.id} 
                className={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 rounded-xl transition-all ${
                  assignment.topping 
                    ? 'bg-white border-2 border-emerald-200 shadow-sm' 
                    : 'bg-white/60 border-2 border-dashed border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2 sm:w-44">
                  <span className="text-sm font-medium text-gray-700">{assignment.rollLabel}</span>
                </div>
                <div className="flex-1">
                  <select
                    value={assignment.topping}
                    onChange={(e) => handleToppingAssignment(index, e.target.value)}
                    className={`w-full px-3 py-2.5 border-2 rounded-lg text-sm transition-all focus:outline-none focus:ring-2 focus:ring-emerald-200 ${
                      assignment.topping 
                        ? 'border-emerald-300 bg-emerald-50' 
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <option value="">Belag wählen...</option>
                    {assignment.topping && !toppingsMenu.some(item => (item.topping_type || item.id) === assignment.topping) && (
                      <option value={assignment.topping}>Belag nicht mehr verfügbar</option>
                    )}
                    {toppingsMenu.map((item) => (
                      <option key={item.id} value={item.topping_type || item.id} disabled={!finitePrice(item.price)}>
                        {toppingLabels[item.topping_type || item.id]}{!finitePrice(item.price) ? ' – Preis nicht verfügbar' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <label className="flex min-h-11 items-center gap-2 text-sm text-gray-700 sm:max-w-[155px]">
                  <input type="checkbox" data-testid={`without-butter-${index}`}
                    aria-label={`${assignment.rollLabel} ohne Butter/Margarine`}
                    checked={Boolean(assignment.withoutButter)}
                    onChange={event => {
                      const withoutButter = event.target.checked;
                      setToppingAssignments(previous => previous.map((entry, entryIndex) => entryIndex === index ? {...entry, withoutButter} : entry));
                    }} className="h-4 w-4 shrink-0 accent-gray-700" />
                  Ohne Butter/Margarine
                </label>
              </div>
            ))}
          </div>
          
          {/* Progress indicator */}
          <div className="mt-4 pt-3 border-t border-emerald-100">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Fortschritt</span>
              <span className={`font-medium ${
                toppingAssignments.filter(a => a.topping).length === totalHalves 
                  ? 'text-emerald-600' 
                  : 'text-amber-600'
              }`}>
                {toppingAssignments.filter(a => a.topping).length} / {totalHalves} Beläge gewählt
              </span>
            </div>
            <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all duration-300 rounded-full ${
                  toppingAssignments.filter(a => a.topping).length === totalHalves 
                    ? 'bg-emerald-500' 
                    : 'bg-amber-400'
                }`}
                style={{ width: `${(toppingAssignments.filter(a => a.topping).length / totalHalves) * 100}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Extras Section */}
      {hasVisibleExtras && (
      <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-2xl p-5 shadow-sm border border-yellow-100">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center text-white font-bold text-sm">+</div>
          <h4 className="font-semibold text-gray-800">Extras hinzufügen</h4>
        </div>
        
        {visibleExtrasCount > 0 && (
        <div className={`grid grid-cols-1 ${visibleExtrasCount > 1 ? 'sm:grid-cols-3' : ''} gap-3`}>
          {/* Boiled Eggs */}
          {visibleBreakfastItems.boiled_eggs && (
          <fieldset disabled={Boolean(priceIssues.boiled_eggs)} className="bg-white rounded-xl p-4 shadow-sm border border-yellow-100">
            <div className="mb-3">
              <span className="font-medium text-gray-700 text-sm block">{eggNames.boiled}</span>
              <span className="text-xs text-gray-500">{priceIssues.boiled_eggs ? '—' : boiledEggsPrice.toFixed(2)} €/Stk</span>
            </div>
            <NumberSelector
              value={boiledEggs}
              onChange={handleBoiledEggsChange}
              min={0}
              max={10}
              unit="Stück"
            />
            {boiledEggs > 0 && (
              <div className="mt-2 text-right text-sm font-medium text-yellow-600">
                = {boiledEggsCost.toFixed(2)} €
              </div>
            )}
          </fieldset>
          )}

          {/* Fried Eggs */}
          {visibleBreakfastItems.fried_eggs && (
          <fieldset disabled={Boolean(priceIssues.fried_eggs)} className="bg-white rounded-xl p-4 shadow-sm border border-yellow-100">
            <div className="mb-3">
              <span className="font-medium text-gray-700 text-sm block">{eggNames.fried}</span>
              <span className="text-xs text-gray-500">{priceIssues.fried_eggs ? '—' : friedEggsPrice.toFixed(2)} €/Stk</span>
            </div>
            <NumberSelector
              value={friedEggs}
              onChange={handleFriedEggsChange}
              min={0}
              max={10}
              unit="Stück"
            />
            {friedEggs > 0 && (
              <div className="mt-2 text-right text-sm font-medium text-yellow-600">
                = {friedEggsCost.toFixed(2)} €
              </div>
            )}
          </fieldset>
          )}

          {/* Scrambled Eggs */}
          {visibleBreakfastItems.scrambled_eggs && (
          <fieldset disabled={Boolean(priceIssues.scrambled_eggs)} className="bg-white rounded-xl p-4 shadow-sm border border-yellow-100">
            <div className="mb-3">
              <span className="font-medium text-gray-700 text-sm block">{eggNames.scrambled}</span>
              <span className="text-xs text-gray-500">{priceIssues.scrambled_eggs ? '—' : scrambledEggsPrice.toFixed(2)} €/Stk</span>
            </div>
            <NumberSelector
              value={scrambledEggs}
              onChange={handleScrambledEggsChange}
              min={0}
              max={10}
              unit="Stück"
            />
            {scrambledEggs > 0 && (
              <div className="mt-2 text-right text-sm font-medium text-yellow-600">
                = {scrambledEggsCost.toFixed(2)} €
              </div>
            )}
          </fieldset>
          )}
        </div>
        )}

        {/* Coffee Option */}
        {visibleBreakfastItems.coffee && (
        <div className="mt-3">
          <label className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all ${
            hasCoffee 
              ? 'bg-amber-100 border-2 border-amber-300' 
              : 'bg-white border-2 border-gray-200 hover:border-amber-200'
          }`}>
            <input
              type="checkbox"
              checked={hasCoffee}
              disabled={Boolean(priceIssues.coffee)}
              onChange={handleCoffeeChange}
              className="sr-only"
            />
            <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${
              hasCoffee 
                ? 'bg-amber-500 border-amber-500' 
                : 'border-gray-300 bg-white'
            }`}>
              {hasCoffee && <span className="text-white text-sm">✓</span>}
            </div>
            <div className="flex-1">
              <span className="font-medium text-gray-700">Kaffee</span>
              <span className="text-sm text-gray-500 ml-2">{coffeeIncluded ? 'In Monatsflat enthalten' : (priceIssues.coffee ? 'Preis nicht verfügbar' : `${coffeePrice.toFixed(2)} € / Tag`)}</span>
            </div>
            {hasCoffee && (
              <span className="text-amber-600 font-medium">{coffeeCost.toFixed(2)} €</span>
            )}
          </label>
        </div>
        )}
      </div>
      )}
      </fieldset>
      )}

      {/* Lunch Option */}
      {mealVisibility.lunch && lunchLockNotice && (
        <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
          {lunchLockNotice}
        </p>
      )}
      {mealVisibility.lunch && (lunchOpen || isEditing) && (
      <fieldset disabled={lunchDisabled} className={`rounded-2xl p-5 shadow-sm border-2 transition-all ${lunchDisabled ? 'opacity-70 ' : ''}${
        hasLunch 
          ? 'bg-gradient-to-br from-violet-100 to-purple-100 border-violet-300' 
          : 'bg-gradient-to-br from-violet-50 to-purple-50 border-violet-100'
      }`}>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={hasLunch}
            onChange={handleLunchChange}
            className="sr-only"
            data-testid="lunch-checkbox"
          />
          <div className={`w-7 h-7 rounded-lg border-2 flex items-center justify-center transition-all ${
            hasLunch 
              ? 'bg-violet-500 border-violet-500' 
              : 'border-violet-300 bg-white'
          }`}>
            {hasLunch && <span className="text-white">✓</span>}
          </div>
          <div className="flex-1">
            <span className="font-semibold text-gray-800">Mittagessen</span>
            {lunchVariants.length <= 1 && (
              todayLunchName ? (
                <span className="block text-sm text-gray-500">Heute: <span className="text-violet-600 font-medium">{todayLunchName}</span>{lunchPrice > 0 ? ` — ${lunchPrice.toFixed(2)} €` : ''}</span>
              ) : lunchPrice > 0 ? (
                <span className="block text-sm text-gray-500">{lunchPrice.toFixed(2)} €</span>
              ) : (
                <span className="block text-sm text-gray-500">Preis wird vom Admin festgelegt</span>
              )
            )}
            {lunchVariants.length >= 2 && (
              <span className="block text-sm text-gray-500">{lunchVariants.length} Optionen zur Auswahl</span>
            )}
          </div>
        </label>
        {hasLunch && lunchVariants.length >= 2 && (
          <div className="mt-4 pl-2 sm:pl-10 space-y-2" data-testid="lunch-variant-picker">
            <p className="text-xs text-gray-500 mb-1">Portionen pro Variante wählen — auch mischbar:</p>
            {lunchVariants.map(v => {
              const cnt = parseInt(lunchCounts[v.id]) || 0;
              return (
                <div key={v.id} className={`flex flex-wrap items-center justify-between gap-2 p-3 rounded-xl border-2 transition-all ${cnt > 0 ? 'bg-white border-violet-400' : 'bg-white/60 border-transparent'}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-medium text-gray-800 truncate">{v.name}</span>
                    </div>
                    <div className="text-xs text-gray-500">{(v.price || 0).toFixed(2)} €</div>
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    <button type="button" onClick={() => setLunchCountFor(v.id, -1)} disabled={cnt === 0} className="w-8 h-8 rounded-lg bg-white border border-violet-300 hover:bg-violet-50 text-violet-700 font-semibold disabled:opacity-40" data-testid={`lunch-variant-${v.id}-dec`}>−</button>
                    <span className="min-w-[2ch] text-center font-semibold text-gray-800" data-testid={`lunch-variant-${v.id}-count`}>{cnt}</span>
                    <button type="button" onClick={() => setLunchCountFor(v.id, +1)} className="w-8 h-8 rounded-lg bg-white border border-violet-300 hover:bg-violet-50 text-violet-700 font-semibold" data-testid={`lunch-variant-${v.id}-inc`}>+</button>
                  </div>
                </div>
              );
            })}
            <div className="text-xs text-violet-700 font-medium text-right">
              Gesamt: {lunchMixTotal} {lunchMixTotal === 1 ? 'Portion' : 'Portionen'} · {lunchMixCost.toFixed(2)} €
            </div>
          </div>
        )}
        {hasLunch && lunchVariants.length < 2 && (
          <div className="mt-3 pl-10 flex items-center gap-3">
            <span className="text-sm text-gray-600">Portionen:</span>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setLunchCount(Math.max(1, lunchCount - 1))} className="w-8 h-8 rounded-lg bg-white border border-violet-300 hover:bg-violet-50 text-violet-700 font-semibold" data-testid="lunch-count-decrement">−</button>
              <span className="min-w-[2ch] text-center font-semibold text-gray-800" data-testid="lunch-count-value">{lunchCount}</span>
              <button type="button" onClick={() => setLunchCount(Math.min(20, lunchCount + 1))} className="w-8 h-8 rounded-lg bg-white border border-violet-300 hover:bg-violet-50 text-violet-700 font-semibold" data-testid="lunch-count-increment">+</button>
            </div>
            {lunchCount > 1 && (
              <span className="text-xs text-violet-600 ml-1">
                = {(effectiveLunchPrice * lunchCount).toFixed(2)} €
              </span>
            )}
          </div>
        )}
      </fieldset>
      )}
      </>

      {/* Phase 3: Abendessen-Card (immer unabhängig sichtbar, wenn Dept aktiviert) */}
      {dinnerLockNotice && (
        <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
          {dinnerLockNotice}
        </p>
      )}
      {dinnerEnabled && (dinnerOpen || isEditing) && (
        <fieldset disabled={dinnerDisabled} className={`rounded-2xl p-5 shadow-sm border-2 transition-all ${dinnerDisabled ? 'opacity-70 ' : ''}${
          hasDinner
            ? 'bg-gradient-to-br from-indigo-100 to-purple-100 border-indigo-300'
            : 'bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-100'
        }`} data-testid="dinner-card">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={hasDinner}
              onChange={(e) => setHasDinner(e.target.checked)}
              className="sr-only"
              data-testid="dinner-checkbox"
            />
            <div className={`w-7 h-7 rounded-lg border-2 flex items-center justify-center transition-all ${
              hasDinner ? 'bg-indigo-500 border-indigo-500' : 'border-indigo-300 bg-white'
            }`}>
              {hasDinner && <span className="text-white">✓</span>}
            </div>
            <div className="flex-1">
              <span className="font-semibold text-gray-800">Abendessen</span>
              {dinnerVariants.length <= 1 && (
                dinnerName ? (
                  <span className="block text-sm text-gray-500">Heute: <span className="text-indigo-600 font-medium">{dinnerName}</span>{dinnerPrice > 0 ? ` — ${dinnerPrice.toFixed(2)} €` : ''}</span>
                ) : dinnerPrice > 0 ? (
                  <span className="block text-sm text-gray-500">{dinnerPrice.toFixed(2)} €</span>
                ) : (
                  <span className="block text-sm text-gray-500">Preis wird vom Admin festgelegt</span>
                )
              )}
              {dinnerVariants.length >= 2 && (
                <span className="block text-sm text-gray-500">{dinnerVariants.length} Optionen zur Auswahl</span>
              )}
            </div>
          </label>
          {hasDinner && dinnerVariants.length >= 2 && (
            <div className="mt-4 pl-2 sm:pl-10 space-y-2" data-testid="dinner-variant-picker">
              <p className="text-xs text-gray-500 mb-1">Portionen pro Variante wählen — auch mischbar:</p>
              {dinnerVariants.map(v => {
                const cnt = parseInt(dinnerCounts[v.id]) || 0;
                return (
                  <div key={v.id} className={`flex flex-wrap items-center justify-between gap-2 p-3 rounded-xl border-2 transition-all ${cnt > 0 ? 'bg-white border-indigo-400' : 'bg-white/60 border-transparent'}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-medium text-gray-800 truncate">{v.name}</span>
                      </div>
                      <div className="text-xs text-gray-500">{(v.price || 0).toFixed(2)} €</div>
                    </div>
                    <div className="flex items-center gap-2 ml-auto">
                      <button type="button" onClick={() => setDinnerCountFor(v.id, -1)} disabled={cnt === 0} className="w-8 h-8 rounded-lg bg-white border border-indigo-300 hover:bg-indigo-50 text-indigo-700 font-semibold disabled:opacity-40" data-testid={`dinner-variant-${v.id}-dec`}>−</button>
                      <span className="min-w-[2ch] text-center font-semibold text-gray-800" data-testid={`dinner-variant-${v.id}-count`}>{cnt}</span>
                      <button type="button" onClick={() => setDinnerCountFor(v.id, +1)} className="w-8 h-8 rounded-lg bg-white border border-indigo-300 hover:bg-indigo-50 text-indigo-700 font-semibold" data-testid={`dinner-variant-${v.id}-inc`}>+</button>
                    </div>
                  </div>
                );
              })}
              <div className="text-xs text-indigo-700 font-medium text-right">
                Gesamt: {dinnerMixTotal} {dinnerMixTotal === 1 ? 'Portion' : 'Portionen'} · {dinnerMixCost.toFixed(2)} €
              </div>
            </div>
          )}
          {hasDinner && dinnerVariants.length < 2 && (
            <div className="mt-3 pl-10 flex items-center gap-3">
              <span className="text-sm text-gray-600">Portionen:</span>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setDinnerCount(Math.max(1, dinnerCount - 1))} className="w-8 h-8 rounded-lg bg-white border border-indigo-300 hover:bg-indigo-50 text-indigo-700 font-semibold" data-testid="dinner-count-decrement">−</button>
                <span className="min-w-[2ch] text-center font-semibold text-gray-800" data-testid="dinner-count-value">{dinnerCount}</span>
                <button type="button" onClick={() => setDinnerCount(Math.min(20, dinnerCount + 1))} className="w-8 h-8 rounded-lg bg-white border border-indigo-300 hover:bg-indigo-50 text-indigo-700 font-semibold" data-testid="dinner-count-increment">+</button>
              </div>
              {dinnerCount > 1 && (
                <span className="text-xs text-indigo-600 ml-1">
                  = {(effectiveDinnerPrice * dinnerCount).toFixed(2)} €
                </span>
              )}
            </div>
          )}
        </fieldset>
      )}

      {/* Notes Field */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-200">
        <label className="font-medium text-gray-700 block mb-3">Extras & Sonderwünsche</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={breakfastDisabled}
          placeholder="z.B. keine Butter, Brötchen nicht schneiden..."
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 resize-none text-sm transition-all"
          rows={2}
        />
      </div>

      {/* Order Summary */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-5 shadow-lg text-white">
        <h4 className="font-bold text-lg mb-4">Bestellübersicht</h4>
        {preservedOrderTotal !== null && <p className="text-sm text-gray-300">Nur Zubereitung geändert · Betrag unverändert</p>}
        
        <div className="space-y-2">
          {preservedOrderTotal === null && <>
          {totalHalves > 0 && (
            <div className="flex justify-between items-center py-1">
              <span className="text-gray-300">Brötchen ({totalHalves} Hälften){toppingsCost > 0 ? ' + Belag' : ''}</span>
              <span className="font-medium">{unavailableTopping ? '—' : `${(rollsCost + toppingsCost).toFixed(2)} €`}</span>
            </div>
          )}
          {boiledEggs > 0 && (
            <div className="flex justify-between items-center py-1">
              <span className="text-gray-300">{eggNames.boiled} ({boiledEggs}x)</span>
              <span className="font-medium">{boiledEggsCost.toFixed(2)} €</span>
            </div>
          )}
          {friedEggs > 0 && (
            <div className="flex justify-between items-center py-1">
              <span className="text-gray-300">{eggNames.fried} ({friedEggs}x)</span>
              <span className="font-medium">{friedEggsCost.toFixed(2)} €</span>
            </div>
          )}
          {scrambledEggs > 0 && (
            <div className="flex justify-between items-center py-1">
              <span className="text-gray-300">{eggNames.scrambled} ({scrambledEggs}x)</span>
              <span className="font-medium">{scrambledEggsCost.toFixed(2)} €</span>
            </div>
          )}
          {hasCoffee && (
            <div className="flex justify-between items-center py-1">
              <span className="text-gray-300">Kaffee</span>
              <span className="font-medium">{coffeeCost.toFixed(2)} €</span>
            </div>
          )}
          {hasLunch && (
            <div className="py-1">
              {lunchMixMode ? (
                <>
                  {lunchVariants.filter(v => (parseInt(lunchCounts[v.id]) || 0) > 0).map(v => (
                    <div key={v.id} className="flex justify-between items-center">
                      <span className="text-gray-300">Mittagessen: {v.name} × {parseInt(lunchCounts[v.id]) || 0}</span>
                      <span className="font-medium">{((v.price || 0) * (parseInt(lunchCounts[v.id]) || 0)).toFixed(2)} €</span>
                    </div>
                  ))}
                  {lunchMixTotal === 0 && (
                    <div className="text-violet-300 text-sm">Bitte Portionen wählen</div>
                  )}
                </>
              ) : (
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">
                    {selectedLunchVariant ? `Mittagessen: ${selectedLunchVariant.name}` : (todayLunchName ? `Mittagessen: ${todayLunchName}` : 'Mittagessen')}
                    {lunchCount > 1 && ` × ${lunchCount}`}
                  </span>
                  {effectiveLunchPrice > 0 ? (
                    <span className="font-medium">{lunchCost.toFixed(2)} €</span>
                  ) : (
                    <span className="text-violet-300 text-sm">Preis wird hinzugefügt</span>
                  )}
                </div>
              )}
            </div>
          )}
          {hasDinner && (
            <div className="py-1">
              {dinnerMixMode ? (
                <>
                  {dinnerVariants.filter(v => (parseInt(dinnerCounts[v.id]) || 0) > 0).map(v => (
                    <div key={v.id} className="flex justify-between items-center">
                      <span className="text-gray-300">Abendessen: {v.name} × {parseInt(dinnerCounts[v.id]) || 0}</span>
                      <span className="font-medium">{((v.price || 0) * (parseInt(dinnerCounts[v.id]) || 0)).toFixed(2)} €</span>
                    </div>
                  ))}
                  {dinnerMixTotal === 0 && (
                    <div className="text-indigo-300 text-sm">Bitte Portionen wählen</div>
                  )}
                </>
              ) : (
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">
                    {selectedDinnerVariant ? `Abendessen: ${selectedDinnerVariant.name}` : (dinnerName ? `Abendessen: ${dinnerName}` : 'Abendessen')}
                    {dinnerCount > 1 && ` × ${dinnerCount}`}
                  </span>
                  {effectiveDinnerPrice > 0 ? (
                    <span className="font-medium">{dinnerCost.toFixed(2)} €</span>
                  ) : (
                    <span className="text-indigo-300 text-sm">Preis wird hinzugefügt</span>
                  )}
                </div>
              )}
            </div>
          )}
          
          </>}
          {/* Total */}
          <div className="border-t border-gray-600 pt-3 mt-3">
            <div className="flex justify-between items-center">
              <span className="font-bold text-lg">Gesamt</span>
              <span className="font-bold text-2xl text-emerald-400">{unavailableTopping || pricesPending ? '—' : `${(preservedOrderTotal ?? totalCost).toFixed(2)} €`}</span>
            </div>
            {hasLunch && (
              <p className="text-xs text-gray-400 mt-1 text-right">+ Mittagessen</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Drinks Order Form
const DrinksOrderForm = ({ drinksMenu, onUpdateQuantity, quantities = {}, title = 'Getränke' }) => {
  const handleQuantityChange = useCallback((drinkId, newQuantity) => {
    onUpdateQuantity(drinkId, newQuantity);
  }, [onUpdateQuantity]);

  // Calculate total
  const totalCost = drinksMenu.reduce((sum, drink) => {
    const qty = quantities[drink.id] || 0;
    return sum + (drink.price * qty);
  }, 0);

  const totalItems = Object.values(quantities).reduce((sum, qty) => sum + qty, 0);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="text-center pb-2">
        <h3 className="text-xl font-bold text-gray-800">{title} auswählen</h3>
        <p className="text-sm text-gray-500 mt-1">Wähle die gewünschte Anzahl aus</p>
      </div>

      {/* Drinks Grid */}
      <div className="bg-gradient-to-br from-slate-50 to-gray-100 rounded-2xl p-5 shadow-sm border border-slate-200">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {drinksMenu.map((drink) => (
            <DrinkItem
              key={drink.id}
              drink={drink}
              quantity={quantities[drink.id] || 0}
              onQuantityChange={handleQuantityChange}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// Drink Item Component
const DrinkItem = ({ drink, quantity, onQuantityChange }) => {
  const handleChange = useCallback((value) => {
    onQuantityChange(drink.id, value);
  }, [drink.id, onQuantityChange]);

  // Calculate max quantity based on stock
  const maxQuantity = drink.stock_enabled ? drink.stock_quantity : 50;
  const isOutOfStock = drink.stock_enabled && drink.stock_quantity === 0;
  const invalidPrice = !validPrice(drink.price);

  return (
    <div className={`bg-white rounded-xl p-4 shadow-sm border transition-all ${
      isOutOfStock 
        ? 'border-red-300 bg-red-50 opacity-60' 
        : quantity > 0
          ? 'border-blue-300 ring-2 ring-blue-100'
          : 'border-slate-200 hover:border-slate-300'
    }`}>
      <div className="flex flex-col space-y-3">
        <div className="flex items-center justify-between">
          <div className={`font-medium ${isOutOfStock ? 'text-gray-400' : 'text-gray-800'}`}>
            {drink.name}
          </div>
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
            {invalidPrice ? 'Preis nicht verfügbar' : `${drink.price.toFixed(2)} €`}
          </span>
        </div>
        
        {/* Stock indicator */}
        {drink.stock_enabled && (
          <div className={`text-xs ${
            isOutOfStock 
              ? 'text-red-500 font-medium' 
              : drink.stock_quantity <= 5 
                ? 'text-yellow-600' 
                : 'text-green-600'
          }`}>
            {isOutOfStock 
              ? 'Nicht verfügbar' 
              : `Noch ${drink.stock_quantity} verfügbar`}
          </div>
        )}
        
        <div className="flex justify-center">
          {isOutOfStock || invalidPrice ? (
            <div className="text-red-500 text-sm font-medium py-2 text-center">
              <div>{invalidPrice ? 'Preis nicht verfügbar' : 'Ausverkauft'}</div>
              {quantity > 0 && (
                <button type="button" onClick={() => handleChange(0)} className="mt-2 underline">
                  Auswahl entfernen
                </button>
              )}
            </div>
          ) : (
            <NumberSelector
              value={quantity}
              onChange={handleChange}
              min={0}
              max={maxQuantity}
            />
          )}
        </div>
        
        {quantity > 0 && !isOutOfStock && (
          <div className="text-right text-sm font-medium text-blue-600">
            = {(drink.price * quantity).toFixed(2)} €
          </div>
        )}
      </div>
    </div>
  );
};

// Sweets Order Form
const SweetsOrderForm = ({ sweetsMenu, onUpdateQuantity }) => {
  const [quantities, setQuantities] = useState({});

  const handleQuantityChange = useCallback((sweetId, newQuantity) => {
    setQuantities(prev => ({ ...prev, [sweetId]: newQuantity }));
    onUpdateQuantity(sweetId, newQuantity);
  }, [onUpdateQuantity]);

  // Calculate total
  const totalCost = sweetsMenu.reduce((sum, sweet) => {
    const qty = quantities[sweet.id] || 0;
    return sum + (sweet.price * qty);
  }, 0);

  const totalItems = Object.values(quantities).reduce((sum, qty) => sum + qty, 0);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="text-center pb-2">
        <h3 className="text-xl font-bold text-gray-800">Snacks auswählen</h3>
        <p className="text-sm text-gray-500 mt-1">Wähle deine Snacks aus</p>
      </div>

      {/* Snacks Grid */}
      <div className="bg-gradient-to-br from-slate-50 to-gray-100 rounded-2xl p-5 shadow-sm border border-slate-200">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sweetsMenu.map((sweet) => (
            <SweetItem
              key={sweet.id}
              sweet={sweet}
              quantity={quantities[sweet.id] || 0}
              onQuantityChange={handleQuantityChange}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// Sweet Item Component
const SweetItem = ({ sweet, quantity, onQuantityChange }) => {
  const handleChange = useCallback((value) => {
    onQuantityChange(sweet.id, value);
  }, [sweet.id, onQuantityChange]);

  // Calculate max quantity based on stock
  const maxQuantity = sweet.stock_enabled ? sweet.stock_quantity : 50;
  const isOutOfStock = sweet.stock_enabled && sweet.stock_quantity === 0;
  const invalidPrice = !validPrice(sweet.price);

  return (
    <div className={`bg-white rounded-xl p-4 shadow-sm border transition-all ${
      isOutOfStock 
        ? 'border-red-300 bg-red-50 opacity-60' 
        : quantity > 0
          ? 'border-blue-300 ring-2 ring-blue-100'
          : 'border-slate-200 hover:border-slate-300'
    }`}>
      <div className="flex flex-col space-y-3">
        <div className="flex items-center justify-between">
          <div className={`font-medium ${isOutOfStock ? 'text-gray-400' : 'text-gray-800'}`}>
            {sweet.name}
          </div>
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
            {invalidPrice ? 'Preis nicht verfügbar' : `${sweet.price.toFixed(2)} €`}
          </span>
        </div>
        
        {/* Stock indicator */}
        {sweet.stock_enabled && (
          <div className={`text-xs ${
            isOutOfStock 
              ? 'text-red-500 font-medium' 
              : sweet.stock_quantity <= 5 
                ? 'text-yellow-600' 
                : 'text-green-600'
          }`}>
            {isOutOfStock 
              ? 'Nicht verfügbar' 
              : `Noch ${sweet.stock_quantity} verfügbar`}
          </div>
        )}
        
        <div className="flex justify-center">
          {isOutOfStock || invalidPrice ? (
            <div className="text-red-500 text-sm font-medium py-2">{invalidPrice ? 'Preis nicht verfügbar' : 'Ausverkauft'}</div>
          ) : (
            <NumberSelector
              value={quantity}
              onChange={handleChange}
              min={0}
              max={maxQuantity}
            />
          )}
        </div>
        
        {quantity > 0 && !isOutOfStock && (
          <div className="text-right text-sm font-medium text-blue-600">
            = {(sweet.price * quantity).toFixed(2)} €
          </div>
        )}
      </div>
    </div>
  );
};

// New Employee Modal
export const NewEmployeeModal = ({ onCreate, onClose, categories = [] }) => {
  const { isSingleDepartment, loading: stationModeLoading } = useStationMode();
  const allowCrossDepartment = !isSingleDepartment && !stationModeLoading;
  const [name, setName] = useState('');
  const [is8HService, setIs8HService] = useState(false);
  const [categoryId, setCategoryId] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name.trim()) {
      onCreate(name.trim(), false, allowCrossDepartment && is8HService, categoryId || null);
      setName('');
      setIs8HService(false);
      setCategoryId('');
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 py-8 overflow-y-auto"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold mb-4">Neuen Mitarbeiter hinzufügen</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
              autoFocus
              required
            />
          </div>
          
          {allowCrossDepartment && <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Typ</label>
            <div className="grid grid-cols-2 gap-2 rounded-lg bg-gray-100 p-1">
              <button
                type="button"
                onClick={() => setIs8HService(false)}
                className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  !is8HService ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Regulär
              </button>
              <button
                type="button"
                onClick={() => setIs8HService(true)}
                className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  is8HService ? 'bg-white text-orange-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Übergreifend
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Übergreifende Mitarbeiter erscheinen in allen WA-Dashboards und nutzen dort eigene Subkonten.
            </p>
          </div>}

          {categories.length > 0 && (
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Kategorie</label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-blue-500"
              >
                <option value="">— Ohne Kategorie —</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Kategorien dienen nur der Anzeige und Filterung im Dashboard.
              </p>
            </div>
          )}
          <div className="flex gap-4">
            <button
              type="submit"
              className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
            >
              Erstellen
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600"
            >
              Abbrechen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Employee Profile List Component
const EmployeeProfileList = ({ employees, onClose }) => {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeProfile, setEmployeeProfile] = useState(null);

  const fetchEmployeeProfile = async (employeeId) => {
    try {
      const response = await axios.get(`${API}/employees/${employeeId}/profile`);
      setEmployeeProfile(response.data);
      setSelectedEmployee(employeeId);
    } catch (error) {
      console.error('Fehler beim Laden des Mitarbeiterprofils:', error);
    }
  };

  if (selectedEmployee && employeeProfile) {
    return (
      <EmployeeProfileDetail
        profile={employeeProfile}
        onBack={() => {
          setSelectedEmployee(null);
          setEmployeeProfile(null);
        }}
        onClose={onClose}
      />
    );
  }

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 py-8 overflow-y-auto"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">Mitarbeiter Profile</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-xl"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {employees.map((employee) => (
              <div
                key={employee.id}
                onClick={() => fetchEmployeeProfile(employee.id)}
                className="bg-white border border-gray-300 rounded-lg p-4 cursor-pointer hover:shadow-lg transition-shadow duration-300"
              >
                <h3 className="text-lg font-semibold mb-2">{employee.name}</h3>
                <div className="text-sm text-gray-600">
                  <p>Essen: {employee.breakfast_balance.toFixed(2)} €</p>
                  <p>Getränke/Snacks: {employee.drinks_sweets_balance.toFixed(2)} €</p>
                  <p className="text-blue-600 mt-2">Klicken für Verlauf →</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Employee Profile Detail Component
const EmployeeProfileDetail = ({ profile, onBack, onClose }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Berlin'
    });
  };

  const getOrderTypeLabel = (orderType) => {
    switch (orderType) {
      case 'breakfast': return 'Essen';
      case 'drinks': return 'Getränke';
      case 'sweets': return 'Snacks';
      default: return orderType;
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 py-8 overflow-y-auto"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ← Zurück
              </button>
              <h2 className="text-2xl font-bold">{profile.employee.name} - Profil</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-xl"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Summary Stats - 50/50 Layout Only */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className={`border border-gray-300 rounded-lg p-4 ${
              profile.breakfast_total >= 0 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <h3 className={`font-semibold ${
                profile.breakfast_total >= 0 
                  ? 'text-green-800' 
                  : 'text-red-800'
              }`}>Essen Saldo</h3>
              <p className={`text-2xl font-bold ${
                profile.breakfast_total >= 0 
                  ? 'text-green-600' 
                  : 'text-red-600'
              }`}>{profile.breakfast_total.toFixed(2)} €</p>
            </div>
            <div className={`border border-gray-300 rounded-lg p-4 ${
              profile.drinks_sweets_total >= 0 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <h3 className={`font-semibold ${
                profile.drinks_sweets_total >= 0 
                  ? 'text-green-800' 
                  : 'text-red-800'
              }`}>Getränke/Snacks Saldo</h3>
              <p className={`text-2xl font-bold ${
                profile.drinks_sweets_total >= 0 
                  ? 'text-green-600' 
                  : 'text-red-600'
              }`}>{profile.drinks_sweets_total.toFixed(2)} €</p>
            </div>
          </div>

          {/* Order History */}
          <div>
            <h3 className="text-xl font-semibold mb-4">Bestellverlauf</h3>
            {profile.order_history.length === 0 ? (
              <p className="text-gray-600">Keine Bestellungen vorhanden</p>
            ) : (
              <div className="space-y-4">
                {profile.order_history.map((order, index) => (
                  <div key={order.id || index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold mr-2 px-2.5 py-0.5 rounded">
                          {getOrderTypeLabel(order.order_type)}
                        </span>
                        <span className="text-sm text-gray-600">{formatDate(order.timestamp)}</span>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold ${order.total_price < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {order.total_price < 0 ? '-' : ''}{Math.abs(order.total_price || 0).toFixed(2)} €
                        </p>
                      </div>
                    </div>
                    
                    {order.readable_items && order.readable_items.length > 0 && (
                      <div className="space-y-1">
                        {order.readable_items.map((item, idx) => (
                          <div key={idx} className="text-sm flex justify-between items-start">
                            <div className="flex-1">
                              <span className="font-medium">{item.description}</span>
                              {item.toppings && <span className="text-gray-600 block text-xs">mit {item.toppings}</span>}
                              {item.unit_price && <span className="text-gray-500 block text-xs">({item.unit_price})</span>}
                            </div>
                            {item.total_price && (
                              <span className="text-sm font-medium text-right ml-2">{item.total_price}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {order.notes && order.notes.trim() !== '' && (
                      <div className="mt-3 pt-3 border-t border-gray-300">
                        <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
                          <span className="text-xs font-medium text-yellow-800 block mb-1">📝 Extras & Sonderwünsche:</span>
                          <span className="text-sm text-yellow-700">{order.notes}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Scroll to Top Button for Modal */}
      {showScrollTop && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            scrollToTop();
          }}
          className="fixed bottom-8 right-8 z-50 p-3 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all duration-300 hover:scale-110"
          aria-label="Nach oben scrollen"
          title="Nach oben"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </button>
      )}
    </div>
  );
};

// Department Admin Dashboard

export default function ProtectedEmployeeMenu(props) {
  const {currentDepartment} = React.useContext(AuthContext);
  return <PersonalOrderGate key={`${currentDepartment.department_id}:${props.employee.id}`} employee={props.employee} departmentId={currentDepartment.department_id} apiBase={API} onClose={props.onClose}>
    {(orderToken,onProtectionRequired)=><EmployeeMenu {...props} orderToken={orderToken} onProtectionRequired={onProtectionRequired}/>}
  </PersonalOrderGate>;
}
